import React, { createContext, useContext } from 'react';
import { BadgeEuro, Battery, Calendar, CheckCircle2, ClipboardCheck, FileText, Globe, Landmark, Mail, MapPin, Phone, Ruler, Shield, ShieldCheck, SunMedium, TrendingUp, Wrench, Zap } from 'lucide-react';
import { DraggableElement } from '../../DraggableElement';
import { EnPowerLogoText, EnPowerWordmarkInline } from '../../EnPowerLogo';

const HideEmptyFieldsContext = createContext(true);

const FALLBACK_HERO = '/solar-farm-bg.jpg';

const DEFAULT_DATA = {
  projektname: 'Dachverpachtung Muster-Logistikzentrum',
  turnkeyProjektname: 'Solaranlage Muster-Logistikzentrum',
  kundenname: 'Muster-Logistik GmbH',
  kundenadresse: 'Industriestraße 42, 06484 Quedlinburg, Sachsen-Anhalt',
  angebotsnummer: 'PR-2025-0019',
  turnkeyAngebotsnummer: 'TK-2025-0042',
  datum: '15. Juni 2025',
  kwp_leistung: '487',
  dachflaeche: '4.200',
  belegbare_flaeche: '3.640',
  spezifischertrag: '950',
  jahresertrag: '462.650',
  kaufpreis: '385.000',
  irr: '7,8 %',
  rendite: '12,5 %',
  payback: '10,4 Jahre',
  status: 'Baufreigabe erteilt',
  inbetriebnahme: 'Q3 2025',
  bundesland: '',
  gemarkung: '',
  flurstueck: '',
  grundbuchblatt: '',
  dachtyp: '',
  netzstatus: '',
  projektImage: '',
  ansprechpartner: 'Randy Wesley Kirsten',
  position: 'Geschäftsführer',
  telefonnummer: '+49 160 8732681',
  email: 'info@en-power.eu',
  website: 'www.en-power.eu',
};

const DEFAULT_PLACEHOLDER_LABELS = {
  projektname: 'Projektname',
  turnkeyProjektname: 'Projektname',
  kundenname: 'Kundenname',
  kundenadresse: 'Kundenadresse',
  angebotsnummer: 'Angebotsnummer',
  turnkeyAngebotsnummer: 'Angebotsnummer',
  datum: 'Datum',
  kwp_leistung: 'Leistung kWp',
  dachflaeche: 'Dachfläche',
  belegbare_flaeche: 'Belegbare Fläche',
  spezifischertrag: 'Spezifischertrag',
  jahresertrag: 'Jahresertrag',
  kaufpreis: 'Kaufpreis',
  irr: 'IRR',
  rendite: 'Rendite p.a.',
  payback: 'Amortisation',
  status: 'Projektstatus',
  inbetriebnahme: 'Inbetriebnahme',
  bundesland: 'Bundesland',
  gemarkung: 'Gemarkung',
  flurstueck: 'Flurstück',
  grundbuchblatt: 'Grundbuchblatt',
  dachtyp: 'Dachtyp',
  netzstatus: 'Netzstatus',
  ansprechpartner: 'Ansprechpartner',
  position: 'Position',
  telefonnummer: 'Telefonnummer',
  email: 'E-Mail',
  website: 'Website',
};

function getPlaceholderLabelForKey(key) {
  return DEFAULT_PLACEHOLDER_LABELS[key] || String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function placeholderToken(label) {
  return `{{${label}}}`;
}

const PLACEHOLDER_DEFAULT_DATA = Object.fromEntries(
  Object.keys(DEFAULT_DATA).map((key) => [key, key === 'projectImage' ? '' : placeholderToken(getPlaceholderLabelForKey(key))])
);

function isPlaceholderMode(data) {
  return data?.placeholder_mode === true;
}

function isPlaceholderToken(raw) {
  return /^\{\{\s*[^}]+?\s*\}\}$/.test(String(raw || '').trim());
}

function mergeExposeData(data = {}) {
  return {
    ...(isPlaceholderMode(data) ? PLACEHOLDER_DEFAULT_DATA : DEFAULT_DATA),
    ...data,
  };
}

function getVariant(variant) {
  return variant === 'expose-turnkey' ? VARIANTS.turnkey : VARIANTS.projektrechte;
}

function stripQuotes(str) {
  let s = str.trim();
  s = s.replace(/[,;]+$/, '').trim();
  s = s.replace(/^[“'«»“”„‟]+|[“'«»“”„‟]+$/g, '').trim();
  return s;
}

function normalizeIsoDate(str) {
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (!m) return str;
  return `${parseInt(m[3], 10)}.${parseInt(m[2], 10)}.${m[1]}`;
}

function normalizeBitrixMoney(str) {
  const m = str.match(/^(-?\d+(?:[.,]\d+)?)\|([A-Z]{3})$/);
  if (!m) return str;
  const num = parseFloat(m[1].replace(',', '.'));
  const currency = m[2] === 'EUR' ? '€' : m[2];
  const formatted = Number.isFinite(num) ? num.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : m[1];
  return `${formatted} ${currency}`;
}

function value(data, key, fallback = '') {
  const raw = data?.[key];
  const cleaned = stripQuotes(String(raw === undefined || raw === null || raw === '' ? fallback : raw).trim());
  return normalizeIsoDate(normalizeBitrixMoney(cleaned));
}

function fieldKey(keys) {
  if (Array.isArray(keys)) return String(keys[0] || '');
  return String(keys || '');
}

const HIDDEN_FIELD_ALIAS_GROUPS = [
  ['projekt_id', 'angebotsnummer', 'turnkeyAngebotsnummer'],
  ['status', 'projektstatus'],
  ['projektname', 'turnkeyProjektname', 'dealTitle'],
  ['kwp_leistung', 'leistung_kwp', 'leistung'],
  ['dachflaeche', 'dachfläche'],
  ['flurstueck', 'flurstück'],
  ['projektrecht_kaufpreis', 'gesamtpreis', 'projektpreis', 'kaufpreis'],
  ['turnkey_kaufpreis', 'gesamtpreis', 'projektpreis', 'kaufpreis'],
  ['preis_projektrecht', 'preis_pro_kwp', 'preis_kwp', 'kaufpreis_pro_kwp'],
  ['turnkey_preis_kwp', 'preis_pro_kwp', 'preis_kwp', 'kaufpreis_pro_kwp'],
  ['telefonnummer', 'telefon'],
  ['projectImage', 'projektbild', 'heroImage', 'image'],
  ['lageplanImage', 'lageplan', 'lageplanBild'],
];

function createHiddenFieldAliasMap(groups) {
  const map = new Map();
  groups.forEach((group) => {
    group.forEach((key) => {
      const current = map.get(key) || [];
      map.set(key, Array.from(new Set([...current, ...group])));
    });
  });
  return map;
}

const HIDDEN_FIELD_ALIAS_MAP = createHiddenFieldAliasMap(HIDDEN_FIELD_ALIAS_GROUPS);

function isFieldHidden(data, key) {
  if (!key) return false;
  const hidden = new Set(Array.isArray(data?.__hiddenFields) ? data.__hiddenFields : []);
  const aliases = HIDDEN_FIELD_ALIAS_MAP.get(key) || [key];
  return aliases.some((alias) => hidden.has(alias));
}

function rowFieldKey(item) {
  return item?.fieldKey || fieldKey(item?.keys);
}

function visibleItems(items, data) {
  return (items || []).filter((item) => !isFieldHidden(data, rowFieldKey(item)));
}

function firstValue(data, keys, fallback = '') {
  for (const key of keys) {
    const resolved = value(data, key);
    if (resolved) return resolved;
  }
  return fallback;
}

function passesVisibleWhen(item, data) {
  if (item.visibleWhen === 'pachtart_jaehrlich') {
    const pachtart = String(firstValue(data, ['pachtart'], '') || '').toLowerCase();
    return pachtart.startsWith('jaehr') || pachtart.startsWith('jährl');
  }
  if (item.visibleWhen === 'grundbuch_belastet') {
    const gb = String(firstValue(data, ['grundbuch_belastet'], '') || '').toLowerCase();
    return gb === 'ja' || gb === 'belastet';
  }
  // Zweiter Kaufpreis (die jeweils andere Angebotsart). Standard ist EIN Preis;
  // diese Zeile erscheint nur, wenn die Version es ausdruecklich verlangt und
  // der Betrag dann auch gesetzt wurde.
  if (item.visibleWhen === 'beide_preise') {
    const flag = String(firstValue(data, ['beide_preise_anzeigen'], '') || '').toLowerCase();
    return flag === 'ja' || flag === 'true';
  }
  return true;
}

function withSuffix(raw, suffix) {
  const text = String(raw || '').trim();
  if (!text || !suffix) return text;
  if (isPlaceholderToken(text)) return text;
  return text.toLowerCase().includes(suffix.toLowerCase()) ? text : `${text} ${suffix}`;
}

const INLINE_FIXED_UNIT_FIELD_CONFIG = {
  kwp_leistung: { suffix: 'kWp', aliases: ['kWp', 'MWp'] },
  leistung_kwp: { suffix: 'kWp', aliases: ['kWp', 'MWp'] },
  leistung: { suffix: 'kWp', aliases: ['kWp', 'MWp'] },
  dachflaeche: { suffix: 'm²', aliases: ['m²', 'm2', 'qm'] },
  belegbare_flaeche: { suffix: 'm²', aliases: ['m²', 'm2', 'qm'] },
  pvsol_generatorflaeche: { suffix: 'm²', aliases: ['m²', 'm2', 'qm'] },
  spezifischertrag: { suffix: 'kWh/kWp/a', aliases: ['kWh/kWp/a', 'kWh/kWp', 'kwh/kwp/a', 'kwh/kwp'] },
  pvsol_jahresertrag_spez: { suffix: 'kWh/kWp', aliases: ['kWh/kWp', 'kWh/kWp/a', 'kwh/kwp', 'kwh/kwp/a'] },
  jahresertrag: { suffix: 'kWh/a', aliases: ['kWh/a', 'kWh/Jahr', 'kwh/a', 'kwh/jahr', 'kWh'] },
  pvsol_netzeinspeisung: { suffix: 'kWh/a', aliases: ['kWh/a', 'kWh/Jahr', 'kwh/a', 'kwh/jahr'] },
  pvsol_generatorleistung: { suffix: 'kWp', aliases: ['kWp', 'MWp'] },
  pvsol_co2: { suffix: 'kg/Jahr', aliases: ['kg/Jahr', 'kg/a'] },
  preis_projektrecht: { suffix: '€/kWp', aliases: ['€/kWp', 'EUR/kWp', '€ pro kWp', 'EUR pro kWp'] },
  turnkey_preis_kwp: { suffix: '€/kWp', aliases: ['€/kWp', 'EUR/kWp', '€ pro kWp', 'EUR pro kWp'] },
  pacht_einmal_kwp: { suffix: '€/kWp', aliases: ['€/kWp', 'EUR/kWp', '€ pro kWp', 'EUR pro kWp'] },
  projektrecht_kaufpreis: { suffix: '€', aliases: ['€', 'EUR'] },
  turnkey_kaufpreis: { suffix: '€', aliases: ['€', 'EUR'] },
  kaufpreis: { suffix: '€', aliases: ['€', 'EUR'] },
  gesamtpreis: { suffix: '€', aliases: ['€', 'EUR'] },
  pacht_einmal_gesamt: { suffix: '€', aliases: ['€', 'EUR'] },
  netto_erloes_25j: { suffix: '€', aliases: ['€', 'EUR'] },
  jahreserloes: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  jaehrliche_zahlung: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  dachpacht: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  versicherung: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  wartung: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  opex: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  eeg_verguetung: { suffix: 'ct/kWh', aliases: ['ct/kWh', 'ct / kWh'] },
  netto_verguetung: { suffix: 'ct/kWh', aliases: ['ct/kWh', 'ct / kWh'] },
  eeg_anteil: { suffix: '%', aliases: ['%'] },
  irr: { suffix: '%', aliases: ['%'] },
  rendite: { suffix: '%', aliases: ['%'] },
  vl_prozent: { suffix: '%', aliases: ['%'] },
};

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function unitPattern(unit) {
  return escapeRegExp(unit).replace(/\\ /g, '\\s*').replace(/\s+/g, '\\s*');
}

function fixedUnitConfigForField(fieldKey) {
  const key = String(fieldKey || '');
  if (/^cashflow_\d+_ertrag$/.test(key)) return { suffix: 'kWh', aliases: ['kWh'] };
  if (/^cashflow_\d+_(erloes|opex|netto)$/.test(key)) return { suffix: '€', aliases: ['€', 'EUR'] };
  return INLINE_FIXED_UNIT_FIELD_CONFIG[key] || null;
}

function stripFixedUnit(value, config) {
  if (!config) return String(value ?? '');
  let text = String(value ?? '').trim();
  const units = [config.suffix, ...(config.aliases || [])]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  units.forEach((unit) => {
    const pattern = new RegExp(`\\s*${unitPattern(unit)}\\s*$`, 'i');
    text = text.replace(pattern, '').trim();
  });

  return text;
}

function normalizeFixedUnitFieldValue(fieldKey, value, fixedSuffix) {
  const config = fixedSuffix === null
    ? null
    : (fixedSuffix ? { suffix: fixedSuffix, aliases: [fixedSuffix] } : fixedUnitConfigForField(fieldKey));
  if (!config) return value;
  let bareValue = stripFixedUnit(value, config).trim();
  // Werte mit drei oder mehr echten Nachkommastellen auf zwei kürzen.
  // WICHTIG: „103.427" ist im Deutschen der Tausenderpunkt (= 103427) und darf NICHT
  // als Dezimalzahl gelesen werden — parseFloat hätte daraus 103,42 gemacht und damit
  // den Faktor 1000 verloren. parseGermanNumber unterscheidet beide Schreibweisen.
  // Komma-Werte bleiben wie bisher unangetastet.
  const num = parseGermanNumber(bareValue);
  const decimalDigits = Number.isFinite(num) ? (String(num).split('.')[1] || '').length : 0;
  if (Number.isFinite(num) && !bareValue.includes(',') && decimalDigits >= 3) {
    bareValue = String(Math.trunc(num * 100) / 100);
  }
  return bareValue ? `${bareValue} ${config.suffix}` : '';
}

function parseGermanNumber(raw) {
  const text = String(raw || '').replace(/[^\d,.-]/g, '').trim();
  if (!text) return NaN;
  const normalized = text.includes(',')
    ? text.replace(/\./g, '').replace(',', '.')
    : text.replace(/(?<=\d)\.(?=\d{3}(\D|$))/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function formatGermanCurrencyPerKwp(value) {
  if (!Number.isFinite(value) || value <= 0) return '';
  return `${Math.round(value).toLocaleString('de-DE')} €/kWp`;
}

// Rohen Zahlwert für die Anzeige mit deutschen Tausenderpunkten formatieren („17937" → „17.937").
// Idempotent: bereits formatierte Werte („9.845,26") bleiben erhalten, die vorhandenen
// Nachkommastellen (max. 2) werden übernommen. Nicht-numerische Werte (Platzhalter, Freitext)
// werden unverändert zurückgegeben.
function formatGermanAmount(raw) {
  const text = String(raw == null ? '' : raw).trim();
  if (!text) return text;
  const parsed = parseGermanNumber(text);
  if (!Number.isFinite(parsed)) return text;
  const decimals = /,\d/.test(text)
    ? Math.min((text.split(',')[1] || '').replace(/\D/g, '').length, 2)
    : 0;
  return parsed.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function computedRowValue(item, data) {
  if (item.compute === 'pricePerKwp') {
    const direct = firstValue(data, item.keys || [], '');
    if (direct) return withSuffix(formatGermanAmount(direct), item.suffix);
    const total = parseGermanNumber(firstValue(data, ['projektrecht_kaufpreis', 'turnkey_kaufpreis', 'gesamtpreis', 'projektpreis', 'kaufpreis', 'capex'], ''));
    const kwp = parseGermanNumber(firstValue(data, ['kwp_leistung', 'leistung_kwp', 'leistung'], ''));
    return formatGermanCurrencyPerKwp(total / kwp) || item.fallback || '';
  }
  if (item.compute === 'totalPrice') {
    const direct = firstValue(data, item.keys || [], '');
    if (direct) return withSuffix(formatGermanAmount(direct), '€');
    // Hochgerechnet wird NUR aus dem €/kWp-Preis derselben Angebotsart, und auch
    // nur wenn die Zeile einen (`perKwpKey`) benennt. Turnkey tut das
    // (`turnkey_preis_kwp` ist ein echter Baupreis je kWp), Projektrechte nicht:
    // `preis_projektrecht` (und die Aliase preis_pro_kwp/preis_kwp/
    // kaufpreis_pro_kwp) traegt bei EN:POWER faktisch den PACHTSATZ — daraus
    // einen „Gesamtpreis" zu bilden ergab die Pachtzahlung an den Eigentuemer,
    // ausgewiesen als Verkaufspreis. Vorgabe des Nutzers (30.07.2026): kein
    // Standardpreis fuer Projektrechte — ohne manuellen Eintrag bleibt leer.
    // Der `perKwpKey` haelt die Hochrechnung ausserdem auf der eigenen Seite:
    // ein Projektrechte-Dokument darf ersatzweise nie den Turnkey-Preis zeigen.
    const ppk = item.perKwpKey
      ? parseGermanNumber(firstValue(data, [item.perKwpKey], ''))
      : NaN;
    const kwp = parseGermanNumber(firstValue(data, ['kwp_leistung', 'leistung_kwp', 'leistung'], ''));
    if (Number.isFinite(ppk) && ppk > 0 && Number.isFinite(kwp) && kwp > 0) {
      return `${Math.round(ppk * kwp).toLocaleString('de-DE')} €`;
    }
    return item.fallback || '';
  }
  if (item.compute === 'pachtkosten') {
    const raw = firstValue(data, item.keys || [], item.fallback);
    if (!raw) return item.fallback || '';
    const pachtart = String(firstValue(data, ['pachtart'], '') || '').toLowerCase();
    // „Sanierung gegen Pacht": NUR den Text ausgeben — kein Betrag, kein „€".
    // Es fliesst kein Geld, die Dachsanierung ist die Gegenleistung. Vorher hing
    // der Text als Suffix HINTER der Zahl; zusammen mit dem Wert
    // „Sanierung gegen Pacht" aus applyAutoCalculations waere daraus
    // „Sanierung gegen Pacht € Sanierung als Pachtzahlung" geworden.
    if (pachtart.includes('sanierung')) {
      return 'Sanierung gegen Pacht';
    }
    const suffix = pachtart.startsWith('einmal') ? 'einmalig'
      : pachtart.startsWith('jaehr') || pachtart.startsWith('jährl') ? 'jährlich'
      : '';
    return withSuffix(withSuffix(formatGermanAmount(raw), '€'), suffix);
  }
  return null;
}

// Betrachtungszeitraum in Jahren aus den Projektdaten (Fallback 20).
function horizontJahre(data) {
  const parsed = parseInt(String(firstValue(data, ['betrachtungszeitraum'], '')).replace(/[^\d]/g, ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 20;
}

function summaryCardLabel(item, data) {
  if (item.dynamicLabel === 'nettoErloesHorizont') return `Netto-Erlös (${horizontJahre(data)} J.)`;
  if (item.dynamicLabel === 'dachflaecheLabel') {
    const source = firstValue(data, ['_dachflaeche_source'], '');
    return source === 'generatorflaeche' ? 'PV-Fläche' : item.label;
  }
  return item.label;
}

function autoInbetriebnahme() {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  const q = Math.floor(month / 3) + 1;
  const qNext = q < 4 ? q + 1 : 1;
  const yearNext = q < 4 ? year : year + 1;
  return year === yearNext
    ? `vrs. Q${q}-Q${qNext} ${year}`
    : `vrs. Q${q} ${year}-Q${qNext} ${yearNext}`;
}

// Marker für das dynamisch aus irr/payback generierte Rendite-Highlight.
const RENDITE_HIGHLIGHT_TOKEN = '{{rendite_highlight}}';

function renditeHighlightText(data) {
  const rendite = parseGermanNumber(firstValue(data, ['rendite', 'irr'], ''));
  const payback = parseGermanNumber(firstValue(data, ['payback'], ''));
  if (!Number.isFinite(rendite) || rendite <= 0) {
    return 'Konservative Kalkulation mit gesicherter EEG-Vergütung über 20 Jahre.';
  }
  const renditeText = rendite.toLocaleString('de-DE', { maximumFractionDigits: 2 });
  const paybackText = Number.isFinite(payback) && payback > 0
    ? ` - Amortisation in ${payback.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Jahren.`
    : '.';
  return `Rendite von ${renditeText} % p.a. bei konservativer Kalkulation${paybackText}`;
}

function resolveHighlights(variantConfig, data) {
  return (variantConfig.highlights || []).map((entry) => (
    entry[2] === RENDITE_HIGHLIGHT_TOKEN ? [entry[0], entry[1], renditeHighlightText(data)] : entry
  ));
}

function getHeroImage(data) {
  return firstValue(data, ['projectImage', 'projektbild', 'heroImage', 'image'], FALLBACK_HERO);
}

function cleanProjectNameForExpose(value) {
  const text = String(value || '').trim();
  if (!text || /\{\{[^}]+\}\}/.test(text)) return text;
  return text
    .replace(/^\s*(?:PVA|PV[-\s]*Anlage|Photovoltaik(?:anlage)?)\s*[-_:]?\s*/i, '')
    .replace(/^\s*\d{1,3}(?:[.\s]\d{3})*(?:,\d+)?\s*(?:kWp|MWp|MW)?\s*[-_/]?\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim() || text;
}

function getProjectName(data, variant) {
  if (variant.key === 'turnkey') {
    return cleanProjectNameForExpose(firstValue(data, ['turnkeyProjektname', 'projektname', 'dealTitle', 'kundenname'], DEFAULT_DATA.turnkeyProjektname));
  }
  return cleanProjectNameForExpose(firstValue(data, ['projektname', 'dealTitle', 'kundenname'], DEFAULT_DATA.projektname));
}

function row(label, keys, fallback, suffix = '') {
  return { label, keys: Array.isArray(keys) ? keys : [keys], fallback, suffix };
}

function listItem(icon, text) {
  return { icon, text };
}

const inlineEditStyle = {
  outline: '1px dashed rgba(224,112,0,0.64)',
  outlineOffset: '2px',
  borderRadius: '3px',
  cursor: 'text',
};

const EditableValue = ({ field, value: displayValue, inlineEditMode = false, onFieldChange, style, fixedSuffix }) => {
  const editable = inlineEditMode && field && typeof onFieldChange === 'function';
  const text = String(displayValue ?? '');
  const unitConfig = fixedSuffix === null
    ? null
    : (fixedSuffix ? { suffix: fixedSuffix, aliases: [fixedSuffix] } : fixedUnitConfigForField(field));
  const editableText = unitConfig ? stripFixedUnit(text, unitConfig) : text;
  if (!editable) return <span style={style}>{text}</span>;

  if (unitConfig) {
    return (
      <span style={{ ...style, display: 'inline-flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: '0.25em' }}>
        <span
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
          style={inlineEditStyle}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            }
          }}
          onBlur={(event) => {
            const next = normalizeFixedUnitFieldValue(field, event.currentTarget.textContent || '', unitConfig.suffix);
            if (next !== text) onFieldChange(field, next);
          }}
        >
          {editableText}
        </span>
        <span contentEditable={false} suppressContentEditableWarning style={{ userSelect: 'none', opacity: 0.78 }}>
          {unitConfig.suffix}
        </span>
      </span>
    );
  }

  return (
    <span
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      style={{ ...style, ...inlineEditStyle }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      onBlur={(event) => {
        const next = event.currentTarget.textContent || '';
        if (next !== text) onFieldChange(field, next);
      }}
    >
      {text}
    </span>
  );
};

const VARIANTS = {
  projektrechte: {
    key: 'projektrechte',
    prefix: 'EXPV',
    badge: 'Projektexposé',
    eyebrow: 'Dach-Photovoltaik • Projektrechte-Verkauf',
    headline: 'PROJEKT\nEXPOSÉ',
    footer: 'Dach-PV • Projektrechte-Exposé • Vertraulich',
    numberKeys: ['angebotsnummer'],
    numberFallback: '',
    statusLabel: 'Projektstatus',
    statusKeys: ['status', 'projektstatus'],
    statusFallback: '',
    coverKpis: [
      row('Leistung', ['kwp_leistung', 'leistung_kwp', 'leistung'], '', 'kWp'),
      { ...row('Dachfläche', ['dachflaeche', 'dachfläche'], '', 'm²'), dynamicLabel: 'dachflaecheLabel' },
      row('Projektstatus', ['status', 'projektstatus'], ''),
    ],
    page2Kpis: [
      row('Leistung', ['kwp_leistung', 'leistung_kwp', 'leistung'], '', 'kWp'),
      { ...row('Dachfläche', ['dachflaeche', 'dachfläche'], '', 'm²'), dynamicLabel: 'dachflaecheLabel' },
      row('Spez. Ertrag', 'spezifischertrag', '', 'kWh/kWp/a'),
      row('Laufzeit', 'pachtlaufzeit', '', 'Jahre'),
    ],
    page2LeftTitle: 'Standort & Gebäudedaten',
    page2LeftRows: [
      row('Adresse', ['kundenadresse', 'standort'], ''),
      row('Gemeinde', 'gemeinde', ''),
      row('Bundesland', 'bundesland', ''),
      row('Gemarkung', 'gemarkung', ''),
      row('Flurstück', ['flurstueck', 'flurstück'], ''),
      row('Dachtyp', 'dachtyp', ''),
    ],
    page2RightTitle: 'Vertrags- & Genehmigungsstatus',
    page2RightRows: [
      row('Dachpachtvertrag', 'pachtvertrag_status', ''),
      row('Netzanschlusszusage', 'netzstatus', ''),
      row('Statikgutachten', 'statikgutachten_status', ''),
      row('Grundbucheintragung', 'grundbucheintragung_status', ''),
    ],
    page3Title: 'Technische Vorplanung',
    page3Rows: [
      row('Modultyp', 'modultyp', ''),
      row('Modulanzahl', 'modulanzahl', ''),
      row('Gesamtleistung', ['kwp_leistung', 'leistung_kwp', 'leistung'], '', 'kWp'),
      row('Wechselrichter', 'wechselrichter', ''),
      row('Netzanschluss', 'netzanschluss', ''),
      row('Einspeisezusage', 'netzstatus', ''),
      row('Spez. Ertrag', 'spezifischertrag', '', 'kWh/kWp/a'),
      row('Jahresertrag', 'jahresertrag', '', 'kWh/a'),
    ],
    commercialTitle: 'Wirtschaftliche Eckdaten',
    commercialRows: [
      { label: 'Gesamtpreis', fieldKey: 'projektrecht_kaufpreis', keys: ['projektrecht_kaufpreis', 'gesamtpreis', 'projektpreis', 'kaufpreis'], fallback: '', compute: 'totalPrice' },
      { label: 'Preis pro kWp', fieldKey: 'preis_projektrecht', keys: ['preis_projektrecht', 'preis_pro_kwp', 'preis_kwp', 'kaufpreis_pro_kwp'], fallback: '', compute: 'pricePerKwp' },
      { label: 'Kaufpreis Turnkey', fieldKey: 'turnkey_kaufpreis', keys: ['turnkey_kaufpreis'], fallback: '', compute: 'totalPrice', perKwpKey: 'turnkey_preis_kwp', visibleWhen: 'beide_preise' },
      row('EEG-Vergütung', 'eeg_verguetung', ''),
      row('Jahreserlös (EEG)', 'jahreserloes', ''),
    ],
    contractRows: [
      row('Pachtvertrag', 'pachtvertrag_status', ''),
      row('Pachtlaufzeit', 'pachtlaufzeit', ''),
      row('Beginn Pachtzahlung', 'pachtbeginn', ''),
      { label: 'Pachtkosten', fieldKey: 'pachtkosten', keys: ['pachtkosten'], fallback: '', compute: 'pachtkosten' },
      row('VL in %', 'vl_prozent', '', '%'),
      row('Verlängerungsoption', 'verlaengerungsoption', ''),
    ],
    networkTitle: 'Netzanschluss',
    networkRows: [
      row('Netzbetreiber', 'netzbetreiber', ''),
      row('Netzanschlusszusage', 'netzstatus', ''),
      row('Anschlussleistung', 'netzanschluss', ''),
      row('Spannungsebene', 'spannungsebene', ''),
      row('Netzverknüpfungspunkt', 'netzverknuepfungspunkt', ''),
      row('Entfernung', 'netzentfernung', ''),
    ],
    pvsolTitle: 'PVSol',
    pvsolRows: [
      row('Standort', 'pvsol_standort', ''),
      row('Quelle der Werte', 'pvsol_quelle', ''),
      row('PV-Generatorleistung', 'pvsol_generatorleistung', ''),
      row('Spez. Jahresertrag', 'pvsol_jahresertrag_spez', ''),
      row('Netzeinspeisung', 'pvsol_netzeinspeisung', ''),
      row('Vermiedene CO₂-Emissionen', 'pvsol_co2', ''),
      row('PV-Generatorfläche', 'pvsol_generatorflaeche', ''),
      row('Anzahl PV-Module', 'pvsol_module', ''),
      row('Anzahl Wechselrichter', 'pvsol_wechselrichter', ''),
    ],
    staticsTitle: 'Statik',
    staticsRows: [
      row('Statik-Vorprüfung durch EN:POWER', 'statik_vorpruefung', ''),
      row('Sanierungsbedarf vorhanden', 'sanierungsbedarf', ''),
      row('Statik-Gutachten', 'statikgutachten_angefordert', ''),
      row('Freigabe Statiker', 'statiker_freigabe', ''),
    ],
    landRegisterTitle: 'Grundbuch',
    landRegisterRows: [
      row('Amtsgericht', 'amtsgericht', ''),
      row('Abdruck vom', 'grundbuch_abdruck_vom', ''),
      row('Grundbuch belastet', 'grundbuch_belastet', ''),
      { ...row('Rangrücktritt', 'rangruecktritt', ''), visibleWhen: 'grundbuch_belastet' },
      row('Grundbucheintragung', 'grundbucheintragung_status', ''),
    ],
    included: [
      'Unterzeichneter Dachpachtvertrag',
      'Netzanschlusszusage',
      'Statikgutachten & Brandschutz',
      'Vollständige technische Planung',
      'Komponentenauswahl & Lieferverträge',
      'Alle Genehmigungen & Gutachten',
    ],
    milestones: [
      ['Standortanalyse & Statik', 'Jan 2025'],
      ['Dachpachtvertrag', 'Feb 2025'],
      ['Netzanschlussantrag', 'Mär 2025'],
      ['Technische Planung', 'Apr 2025'],
      ['Baufreigabe', 'Jun 2025'],
      ['Errichtung', 'Jul-Aug 2025'],
      ['Inbetriebnahme (COD)', 'Sep 2025'],
    ],
    risks: [
      ['Sonneneinstrahlung', 'Niedrig', 'Standort liegt mit 1.050 kWh/m²/a im oberen Bereich für Sachsen-Anhalt. Ertragsgutachten durch unabhängiges Institut bestätigt.'],
      ['Netzanschluss', 'Niedrig', 'Einspeisezusage durch Avacon erteilt. Trafostation im Netzgebiet 200m entfernt. Keine Kapazitätsengpässe bekannt.'],
      ['Dachzustand', 'Niedrig', 'Gebäude Baujahr 2018, Flachdach in einwandfreiem Zustand. Statikgutachten bestätigt 15 kg/m² Reserve.'],
      ['Regulatorisch', 'Niedrig', 'EEG-Vergütung für 20 Jahre nach Inbetriebnahme gesichert. Keine bekannten regulatorischen Risiken.'],
    ],
    highlightsTitle: 'Warum dieses Projekt?',
    highlights: [
      [Shield, 'Baureifes Projekt', 'Alle Genehmigungen erteilt, Pachtvertrag unterzeichnet - sofort umsetzbar.'],
      [Landmark, 'Erstklassiger Standort', 'Neuwertiges Gebäude in attraktiver Lage mit optimaler Südausrichtung.'],
      [TrendingUp, 'Gesicherte Erträge', '20 Jahre EEG-Vergütung mit konservativer Ertragskalkulation.'],
      [BadgeEuro, 'Attraktiver Preis', 'Wettbewerbsfähige Projektmarge bei überdurchschnittlicher Qualität.'],
    ],
  },
  turnkey: {
    key: 'turnkey',
    prefix: 'EXTK',
    badge: 'Turnkey Exposé',
    eyebrow: 'Dach-Photovoltaik • Schlüsselfertige Anlage',
    headline: 'TURNKEY\nEXPOSÉ',
    footer: 'Dach-PV • Turnkey-Exposé • Vertraulich',
    numberKeys: ['turnkeyAngebotsnummer', 'angebotsnummer'],
    numberFallback: '',
    statusLabel: 'Inbetriebnahme',
    statusKeys: ['inbetriebnahme'],
    statusFallback: autoInbetriebnahme(),
    coverKpis: [
      row('Leistung', ['kwp_leistung', 'leistung_kwp', 'leistung'], '', 'kWp'),
      row('Kaufpreis', 'kaufpreis', '', '€'),
      row('Jahresertrag', 'jahresertrag', '', 'kWh'),
    ],
    page2Kpis: [
      row('Leistung', ['kwp_leistung', 'leistung_kwp', 'leistung'], '', 'kWp'),
      { ...row('Dachfläche', ['dachflaeche', 'dachfläche'], '', 'm²'), dynamicLabel: 'dachflaecheLabel' },
      row('Spez. Ertrag', 'spezifischertrag', '', 'kWh/kWp/a'),
      row('Inbetriebnahme', 'inbetriebnahme', ''),
    ],
    page2LeftTitle: 'Standort & Gebäudedaten',
    page2LeftRows: [
      row('Adresse', ['kundenadresse', 'standort'], ''),
      row('Gemeinde', 'gemeinde', ''),
      row('Bundesland', 'bundesland', ''),
      row('Gemarkung', 'gemarkung', ''),
      row('Flurstück', ['flurstueck', 'flurstück'], ''),
      row('Dachtyp', 'dachtyp', ''),
    ],
    page2RightTitle: 'Projekt- & Übergabestatus',
    page2RightRows: [
      row('Netzanschlusszusage', 'netzstatus', ''),
      row('Statikgutachten', 'statikgutachten_status', ''),
      row('Inbetriebnahme', 'inbetriebnahme', ''),
    ],
    page3Title: 'Technische Vorplanung',
    page3Rows: [
      row('Modultyp', 'modultyp', ''),
      row('Modulanzahl', 'modulanzahl', ''),
      row('Gesamtleistung', ['kwp_leistung', 'leistung_kwp', 'leistung'], '', 'kWp'),
      row('Wechselrichter', 'wechselrichter', ''),
      row('Netzanschluss', 'netzanschluss', ''),
      row('Einspeisezusage', 'netzstatus', ''),
      row('Spez. Ertrag', 'spezifischertrag', '', 'kWh/kWp/a'),
      row('Jahresertrag', 'jahresertrag', '', 'kWh/a'),
    ],
    commercialTitle: 'Wirtschaftliche Eckdaten',
    commercialRows: [
      { label: 'Kaufpreis Turnkey', fieldKey: 'turnkey_kaufpreis', keys: ['turnkey_kaufpreis', 'gesamtpreis', 'projektpreis', 'kaufpreis'], fallback: '', compute: 'totalPrice', perKwpKey: 'turnkey_preis_kwp' },
      { label: 'Preis pro kWp', fieldKey: 'turnkey_preis_kwp', keys: ['turnkey_preis_kwp', 'preis_pro_kwp', 'preis_kwp', 'kaufpreis_pro_kwp'], fallback: '', compute: 'pricePerKwp' },
      { label: 'Preis Projektrechte', fieldKey: 'projektrecht_kaufpreis', keys: ['projektrecht_kaufpreis'], fallback: '', compute: 'totalPrice', visibleWhen: 'beide_preise' },
      row('EEG-Vergütung', 'eeg_verguetung', ''),
      row('Jahreserlös (EEG)', 'jahreserloes', ''),
    ],
    contractRows: [
      row('VL in %', 'vl_prozent', '', '%'),
      row('Verlängerungsoption', 'verlaengerungsoption', ''),
      row('O&M optional', 'om_vertrag', ''),
    ],
    networkTitle: 'Netzanschluss',
    networkRows: [
      row('Netzbetreiber', 'netzbetreiber', ''),
      row('Netzanschlusszusage', 'netzstatus', ''),
      row('Anschlussleistung', 'netzanschluss', ''),
      row('Spannungsebene', 'spannungsebene', ''),
      row('Netzverknüpfungspunkt', 'netzverknuepfungspunkt', ''),
      row('Entfernung', 'netzentfernung', ''),
    ],
    pvsolTitle: 'PVSol',
    pvsolRows: [
      row('Standort', 'pvsol_standort', ''),
      row('Quelle der Werte', 'pvsol_quelle', ''),
      row('PV-Generatorleistung', 'pvsol_generatorleistung', ''),
      row('Spez. Jahresertrag', 'pvsol_jahresertrag_spez', ''),
      row('Netzeinspeisung', 'pvsol_netzeinspeisung', ''),
      row('Vermiedene CO₂-Emissionen', 'pvsol_co2', ''),
      row('PV-Generatorfläche', 'pvsol_generatorflaeche', ''),
      row('Anzahl PV-Module', 'pvsol_module', ''),
      row('Anzahl Wechselrichter', 'pvsol_wechselrichter', ''),
    ],
    staticsTitle: 'Statik',
    staticsRows: [
      row('Statik-Vorprüfung durch EN:POWER', 'statik_vorpruefung', ''),
      row('Sanierungsbedarf vorhanden', 'sanierungsbedarf', ''),
      row('Statik-Gutachten', 'statikgutachten_angefordert', ''),
      row('Freigabe Statiker', 'statiker_freigabe', ''),
    ],
    landRegisterTitle: 'Grundbuch',
    landRegisterRows: [
      row('Amtsgericht', 'amtsgericht', ''),
      row('Abdruck vom', 'grundbuch_abdruck_vom', ''),
      row('Grundbuch belastet', 'grundbuch_belastet', ''),
      { ...row('Rangrücktritt', 'rangruecktritt', ''), visibleWhen: 'grundbuch_belastet' },
      row('Grundbucheintragung', 'grundbucheintragung_status', ''),
    ],
    cashflow: [
      ['1', '462.650', '37.151', '-8.200', '28.951'],
      ['5', '455.298', '36.560', '-8.530', '28.030'],
      ['10', '444.248', '35.673', '-9.020', '26.653'],
      ['15', '433.458', '34.807', '-9.540', '25.267'],
      ['20', '422.921', '33.963', '-10.090', '23.873'],
      ['25', '412.631', '33.134', '-10.670', '22.464'],
    ],
    revenueRows: [
      row('EEG-Einspeisevergütung', 'eeg_anteil', ''),
      row('Vergütungssatz', 'eeg_verguetung', ''),
      row('Netto-Vergütung', 'netto_verguetung', ''),
    ],
    opexRows: [
      { ...row('Dachpacht', 'dachpacht', ''), visibleWhen: 'pachtart_jaehrlich' },
      row('Versicherung', 'versicherung', ''),
      row('Wartung & O&M', 'wartung', ''),
      row('Gesamt OPEX p.a.', 'opex', ''),
    ],
    summaryCards: [
      row('Gesamtinvestition', 'turnkey_kaufpreis', '', '€'),
      row('Rendite p.a.', 'rendite', '', '%'),
      row('Amortisation', 'payback', '', 'Jahre'),
      { ...row('Netto-Erlös', 'netto_erloes_25j', '', '€'), dynamicLabel: 'nettoErloesHorizont' },
    ],
    documentation: [
      listItem(FileText, 'Vollständige technische Dokumentation'),
      listItem(ShieldCheck, 'Alle Garantiezertifikate & Prüfprotokolle'),
      listItem(Zap, 'Netzanschlussbestätigung & Einspeisezusage'),
      listItem(Battery, 'Ertragsgutachten & Simulationsbericht'),
      listItem(Wrench, 'O&M-Vertrag (optional, 5/10/15 Jahre)'),
      listItem(TrendingUp, 'Monitoring-Zugang (Huawei FusionSolar)'),
    ],
    highlightsTitle: 'Investitions-Highlights',
    highlights: [
      [TrendingUp, 'EEG-Vergütung gesichert', '20 Jahre garantierte Einspeisevergütung nach EEG 2023 - planbare Cashflows.'],
      [CheckCircle2, 'Schlüsselfertig & betriebsbereit', 'Vollständig installiert, abgenommen und ans Netz angeschlossen - sofort ertragsfähig.'],
      [Shield, 'Erstklassige Komponenten', 'Tier-1 Module und Wechselrichter mit umfangreichen Herstellergarantien.'],
      [BadgeEuro, 'Attraktive Rendite', RENDITE_HIGHLIGHT_TOKEN],
    ],
  },
};

Object.assign(VARIANTS.turnkey, {
  statusLabel: VARIANTS.projektrechte.statusLabel,
  statusKeys: VARIANTS.projektrechte.statusKeys,
  statusFallback: VARIANTS.projektrechte.statusFallback,
  coverKpis: VARIANTS.projektrechte.coverKpis,
  page2Kpis: VARIANTS.projektrechte.page2Kpis,
  page2LeftTitle: VARIANTS.projektrechte.page2LeftTitle,
  page2LeftRows: VARIANTS.projektrechte.page2LeftRows,
  page2RightTitle: VARIANTS.projektrechte.page2RightTitle,
  page2RightRows: VARIANTS.projektrechte.page2RightRows,
  page3Title: VARIANTS.projektrechte.page3Title,
  page3Rows: VARIANTS.projektrechte.page3Rows,
  // commercialTitle/commercialRows werden BEWUSST nicht uebernommen: Turnkey
  // behaelt seine eigenen Preiszeilen (turnkey_kaufpreis / turnkey_preis_kwp).
  // Vorher zog dieser Block die Projektrechte-Preise mit herein — dadurch stand
  // im selben Dokument auf der Seite `company` der Projektrechtspreis und auf
  // `extra` (summaryCards) der Turnkey-Preis, also zwei verschiedene Kaufpreise.
  // Welcher Preis gilt, entscheidet allein die gewaehlte Variante.
  contractRows: VARIANTS.projektrechte.contractRows,
  networkTitle: VARIANTS.projektrechte.networkTitle,
  networkRows: VARIANTS.projektrechte.networkRows,
  pvsolTitle: VARIANTS.projektrechte.pvsolTitle,
  pvsolRows: VARIANTS.projektrechte.pvsolRows,
  staticsTitle: VARIANTS.projektrechte.staticsTitle,
  staticsRows: VARIANTS.projektrechte.staticsRows,
  landRegisterTitle: VARIANTS.projektrechte.landRegisterTitle,
  landRegisterRows: VARIANTS.projektrechte.landRegisterRows,
});

const dragPropsFor = (editMode, showMarkers, selectedElements, elementPositions, onSelectElement, onPositionChange) => ({
  editMode,
  showMarkers,
  selectedElements,
  elementPositions,
  onSelectElement,
  onPositionChange,
  dragAxis: 'y',
});

const pageStyle = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: '#FFFFFF',
};

const Footer = ({ page, variant }) => (
  <div className="page-footer" style={{ marginTop: 'auto', height: '42px', borderTop: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px' }}>
    <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 800, color: '#E07000', letterSpacing: '0.04em' }}>
      <EnPowerWordmarkInline enColor="#122A5C" powerColor="#E07000" fontFamily="Manrope, sans-serif" fontSize="8px" fontWeight={800} />
    </span>
    <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#94A3B8', letterSpacing: '0.02em' }}>{variant.footer}</span>
    <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '8.5px', fontWeight: 700, color: '#94A3B8' }}>{page}</span>
  </div>
);

const PageHeader = ({ variant, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '28px 48px 20px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
      <span style={{ color: '#E07000', fontWeight: 900, fontSize: '16px', lineHeight: 1 }}>•</span>
      <div>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.12em', color: '#E07000', fontWeight: 800, margin: 0 }}>{variant.badge}</p>
        <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 800, color: '#122A5C', margin: '2px 0 0' }}>{title}</p>
      </div>
    </div>
    <EnPowerLogoText size="sm" />
  </div>
);

const SectionHeading = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
    <div style={{ width: '4px', height: '18px', borderRadius: '2px', background: '#E07000' }} />
    <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 800, color: '#122A5C', margin: 0, letterSpacing: '0' }}>{children}</h2>
  </div>
);

const DataTable = ({ rows, data, inlineEditMode = false, onFieldChange }) => {
  const hideEmpty = useContext(HideEmptyFieldsContext);
  const visible = visibleItems(rows, data);
  const filtered = visible.filter((item) => passesVisibleWhen(item, item.data || data));
  const populated = hideEmpty ? filtered.filter((item) => {
    const sourceData = item.data || data;
    const resolved = firstValue(sourceData, item.keys, item.fallback);
    return resolved !== '' && resolved != null;
  }) : filtered;
  if (!populated.length) return null;
  return (
    <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', background: '#FFFFFF' }}>
      {populated.map((item, index) => {
        const sourceData = item.data || data;
        return (
          <div key={`${item.label}-${index}`} style={{ display: 'grid', gridTemplateColumns: '172px 1fr', minHeight: '32px', borderTop: index === 0 ? 'none' : '1px solid #E2E8F0' }}>
            <div style={{ background: '#EDF2F7', padding: '9px 12px', fontFamily: 'Manrope, sans-serif', fontSize: '8.7px', fontWeight: 800, color: '#122A5C' }}>{item.label}</div>
            <div style={{ padding: '9px 12px', fontFamily: 'Manrope, sans-serif', fontSize: '8.8px', fontWeight: 600, color: '#334155', lineHeight: 1.35 }}>
              <EditableValue
                field={rowFieldKey(item)}
                value={withSuffix(firstValue(sourceData, item.keys, item.fallback), item.suffix)}
                inlineEditMode={inlineEditMode}
                onFieldChange={onFieldChange}
                fixedSuffix={item.suffix}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const KpiStrip = ({ items, data, columns = 4, compact = false, inlineEditMode = false, onFieldChange }) => {
  const hideEmpty = useContext(HideEmptyFieldsContext);
  const base = visibleItems(items, data);
  const visible = hideEmpty ? base.filter((item) => {
    const resolved = firstValue(data, item.keys, item.fallback);
    return resolved !== '' && resolved != null;
  }) : base;
  if (!visible.length) return null;
  return (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, Math.min(columns, visible.length || columns))}, 1fr)`, gap: compact ? '8px' : '10px' }}>
    {visible.map((item) => (
      <div key={item.label} style={{ border: '1px solid rgba(224,112,0,0.22)', background: compact ? '#FFFFFF' : 'rgba(224,112,0,0.10)', borderRadius: '6px', padding: compact ? '10px 12px' : '8px 12px' }}>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: compact ? '7.2px' : '7.5px', fontWeight: 800, color: compact ? '#64748B' : 'rgba(255,255,255,0.52)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>{summaryCardLabel(item, data)}</p>
        <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: compact ? '18px' : '14px', lineHeight: 1.05, fontWeight: 800, color: compact ? '#122A5C' : '#FFFFFF', margin: 0 }}>
          <EditableValue
            field={rowFieldKey(item)}
            value={withSuffix(firstValue(data, item.keys, item.fallback), item.suffix)}
            inlineEditMode={inlineEditMode}
            onFieldChange={onFieldChange}
            fixedSuffix={item.suffix}
          />
        </p>
      </div>
    ))}
  </div>
  );
};

const CoverMeta = ({ variant, data, inlineEditMode = false, onFieldChange }) => {
  const hideEmpty = useContext(HideEmptyFieldsContext);
  const base = [
    { label: 'Exposé-Nr.', keys: variant.numberKeys, fallback: variant.numberFallback },
    { label: 'Datum', keys: ['datum'], fallback: DEFAULT_DATA.datum },
    { label: variant.statusLabel, keys: variant.statusKeys, fallback: variant.statusFallback },
  ].filter((item) => !isFieldHidden(data, rowFieldKey(item)));
  const entries = hideEmpty ? base.filter((item) => { const v = firstValue(data, item.keys, item.fallback); return v !== '' && v != null; }) : base;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, entries.length)}, 1fr)`, borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '18px 0' }}>
      {entries.map((item, index) => (
        <div key={item.label} style={{ padding: '0 16px', borderRight: index < entries.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 800, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 5px' }}>{item.label}</p>
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: index === 2 ? '#E07000' : '#FFFFFF', letterSpacing: '0', margin: 0 }}>
            <EditableValue
              field={rowFieldKey(item)}
              value={firstValue(data, item.keys, item.fallback)}
              inlineEditMode={inlineEditMode}
              onFieldChange={onFieldChange}
            />
          </p>
        </div>
      ))}
    </div>
  );
};

function dataRows(rows, data) {
  return rows.map((item) => ({ ...item, data }));
}

function resolvedRowValue(item, data) {
  const computed = computedRowValue(item, data);
  if (computed !== null) return computed;
  return withSuffix(firstValue(data, item.keys, item.fallback), item.suffix);
}

const projectRightsPageStyle = {
  ...pageStyle,
  boxSizing: 'border-box',
  padding: 0,
  gap: '0',
};

const ProjectRightsHeader = ({ projectName, variant, data, inlineEditMode = false, onFieldChange }) => {
  const projectNameField = variant?.key === 'turnkey' ? 'turnkeyProjektname' : 'projektname';
  const showProjectName = !isFieldHidden(data, 'projektname');

  return (
    <div style={{ height: '106px', boxSizing: 'border-box', padding: '45px 48px 0', flex: '0 0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '22px', gap: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <EnPowerLogoText size="sm" variant="full" />
          <span style={{ width: '3px', height: '3px', borderRadius: '999px', background: '#CBD5E1', display: 'inline-block' }} />
          <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#64748B', fontWeight: 650 }}>{variant?.badge || 'Projektexposé'}</span>
        </div>
        {showProjectName ? (
          <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#64748B', fontWeight: 550, textAlign: 'right' }}>
            <EditableValue
              field={projectNameField}
              value={projectName}
              inlineEditMode={inlineEditMode}
              onFieldChange={onFieldChange}
            />
          </span>
        ) : null}
      </div>
      <div style={{ height: '1px', background: '#E2E8F0', marginTop: '18px' }} />
    </div>
  );
};

const ProjectRightsFooter = ({ page, variant }) => (
  <div style={{ height: '62px', flex: '0 0 auto', borderTop: '1px solid #E2E8F0', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center', padding: '0 84px', boxSizing: 'border-box' }}>
    <span style={{ justifySelf: 'start' }}>
      <EnPowerWordmarkInline enColor="#122A5C" powerColor="#E07000" fontFamily="Manrope, sans-serif" fontSize="9px" fontWeight={800} />
    </span>
    <span style={{ justifySelf: 'center', fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#CBD5E1', letterSpacing: '0.02em' }}>{variant?.footer || 'Dach-PV • Projektrechte-Exposé • Vertraulich'}</span>
    <span style={{ justifySelf: 'end', fontFamily: 'Outfit, sans-serif', fontSize: '8.5px', fontWeight: 700, color: '#CBD5E1' }}>{page}</span>
  </div>
);

const ProjectRightsContent = ({ children, top = 37 }) => (
  <div style={{ flex: '1 1 auto', minHeight: 0, boxSizing: 'border-box', padding: `${top}px 48px 0` }}>
    {children}
  </div>
);

const rightsSectionHeadingStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '15px',
};

const RightsSectionHeading = ({ children }) => (
  <div style={rightsSectionHeadingStyle}>
    <span style={{ width: '4px', height: '20px', borderRadius: '3px', background: '#E07000', display: 'inline-block', flex: '0 0 4px' }} />
    <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', lineHeight: 1.08, fontWeight: 800, color: '#06265E', margin: 0, letterSpacing: '0' }}>{children}</h2>
  </div>
);

const rightsCardStyle = {
  border: '1px solid #D8E1EC',
  borderRadius: '9px',
  background: '#F8FAFC',
  overflow: 'hidden',
};

const RightsTable = ({ rows, data, valueAccent = () => false, inlineEditMode = false, onFieldChange }) => {
  const hideEmpty = useContext(HideEmptyFieldsContext);
  const base = visibleItems(rows, data);
  const filtered = base.filter((item) => passesVisibleWhen(item, data));
  const populated = hideEmpty ? filtered.filter((item) => {
    const v = resolvedRowValue(item, data);
    return v !== '' && v != null;
  }) : filtered;
  if (!populated.length) return null;
  return (
  <div style={rightsCardStyle}>
    {populated.map((item, index) => {
      const text = resolvedRowValue(item, data);
      const isAccent = valueAccent(item, text);
      const isOrangeStatus = isAccent && /in bearbeitung/i.test(text);
      const isGreenStatus = isAccent && /positiv|bestätigt|vorbereitet|vorhanden|liegt vor|abgeschlossen/i.test(text);
      const accentColor = isOrangeStatus ? '#E07000' : isGreenStatus ? '#16A34A' : isAccent ? '#E07000' : '#06265E';
      return (
        <div key={`${item.label}-${index}`} style={{ display: 'grid', gridTemplateColumns: '142px 1fr', minHeight: '27px', borderTop: index === 0 ? 'none' : '1px solid #E6EDF5' }}>
          <div style={{ padding: '8px 16px', fontFamily: 'Manrope, sans-serif', fontSize: '8.7px', color: '#475569', lineHeight: 1.15 }}>{item.label}</div>
          <div style={{ padding: '8px 16px 8px 8px', fontFamily: 'Manrope, sans-serif', fontSize: '8.7px', fontWeight: 800, color: accentColor, textAlign: 'right', lineHeight: 1.15 }}>
            <EditableValue
              field={rowFieldKey(item)}
              value={text}
              inlineEditMode={inlineEditMode}
              onFieldChange={onFieldChange}
              fixedSuffix={item.suffix}
            />
          </div>
        </div>
      );
    })}
  </div>
  );
}

const StatusCard = ({ rows, data, inlineEditMode = false, onFieldChange }) => {
  const hideEmpty = useContext(HideEmptyFieldsContext);
  const base = visibleItems(rows, data);
  const populated = hideEmpty ? base.filter((item) => {
    const v = resolvedRowValue(item, data);
    return v !== '' && v != null;
  }) : base;
  if (!populated.length) return null;
  return (
  <div style={{ ...rightsCardStyle, padding: '12px 16px' }}>
    {populated.map((item, index) => {
      const isDachpacht = /dachpacht|pachtvertrag_status/i.test(rowFieldKey(item));
      const text = isDachpacht ? 'liegt vor' : resolvedRowValue(item, data);
      const isOrange = /in bearbeitung/i.test(text);
      return (
        <div key={`${item.label}-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', minHeight: '28px', borderTop: index === 0 ? 'none' : '1px solid #E6EDF5' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontFamily: 'Manrope, sans-serif', fontSize: '8.8px', color: '#475569' }}>
            <CheckCircle2 size={12} color={isOrange ? '#E07000' : '#16A34A'} strokeWidth={2.6} />
            {item.label}
          </div>
          <span style={{ borderRadius: '4px', background: isOrange ? '#FFF7ED' : '#ECFDF3', color: isOrange ? '#E07000' : '#16A34A', padding: '3px 8px', fontFamily: 'Manrope, sans-serif', fontSize: '7.8px', fontWeight: 800 }}>
            <EditableValue
              field={rowFieldKey(item)}
              value={text}
              inlineEditMode={inlineEditMode}
              onFieldChange={onFieldChange}
            />
          </span>
        </div>
      );
    })}
  </div>
  );
}

const rightsKpiIcons = [Zap, Ruler, SunMedium, Calendar];

const RightsKpiStrip = ({ items, data, inlineEditMode = false, onFieldChange }) => {
  const hideEmpty = useContext(HideEmptyFieldsContext);
  const base = visibleItems(items, data);
  const visible = hideEmpty ? base.filter((item) => {
    const resolved = firstValue(data, item.keys, item.fallback);
    return resolved !== '' && resolved != null;
  }) : base;
  if (!visible.length) return null;
  return (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '9px', marginBottom: '26px' }}>
    {visible.map((item, index) => {
      const Icon = rightsKpiIcons[index] || TrendingUp;
      const raw = firstValue(data, item.keys, item.fallback);
      const fixedConfig = item.suffix ? { suffix: item.suffix, aliases: [item.suffix] } : fixedUnitConfigForField(rowFieldKey(item));
      const displayRaw = fixedConfig ? stripFixedUnit(raw, fixedConfig) : raw;
      const showSuffix = item.suffix && !isPlaceholderToken(displayRaw);
      return (
        <div key={item.label} style={{ height: '80px', border: '1px solid #D8E1EC', borderRadius: '9px', background: '#FFFFFF', padding: '15px 16px 12px', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '8px' }}>
            <span style={{ width: '22px', height: '22px', borderRadius: '6px', background: index === 1 || index === 3 ? '#FFF7ED' : '#EEF3F9', display: 'grid', placeItems: 'center' }}>
              <Icon size={12} color={index === 1 || index === 3 ? '#E07000' : '#60708C'} strokeWidth={2.2} />
            </span>
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.8px', fontWeight: 800, color: '#415675', textTransform: 'uppercase', letterSpacing: '0.055em' }}>{summaryCardLabel(item, data)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <EditableValue
              field={rowFieldKey(item)}
              value={displayRaw}
              inlineEditMode={inlineEditMode}
              onFieldChange={(field, nextValue) => onFieldChange?.(field, normalizeFixedUnitFieldValue(field, nextValue, item.suffix || undefined))}
              fixedSuffix={null}
              style={{ fontFamily: 'Outfit, sans-serif', fontSize: '26px', lineHeight: 0.95, fontWeight: 800, color: '#06265E', letterSpacing: '0' }}
            />
            {showSuffix ? <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', color: '#415675', fontWeight: 650 }}>{item.suffix}</span> : null}
          </div>
        </div>
      );
    })}
  </div>
  );
};

const LAGEPLAN_ASPECT = 16 / 9;
const LageplanPlaceholder = ({ height, image = '' }) => (
  <div style={{ width: '92%', margin: '0 auto', aspectRatio: `${LAGEPLAN_ASPECT}`, ...(height ? { height, aspectRatio: undefined } : {}), border: '1px solid #D8E1EC', borderRadius: '9px', background: '#F8FAFC', display: 'grid', placeItems: 'center', color: '#6B7C96', textAlign: 'center', overflow: 'hidden' }}>
    {image ? (
      <img src={image} alt="Lageplan" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center center', display: 'block' }} />
    ) : (
      <div>
      <MapPin size={25} color="#6B7C96" strokeWidth={2.1} />
      <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', fontWeight: 750, color: '#667794', margin: '4px 0 0' }}>Lageplan</p>
      <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.6px', color: '#A4AFBF', margin: '1px 0 0' }}>wird eingefügt</p>
      </div>
    )}
  </div>
);

const IncludedPanel = ({ items }) => (
  <div style={{ borderRadius: '11px', background: '#122A5C', padding: '20px 25px 19px', color: '#FFFFFF' }}>
    <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 800, margin: '0 0 13px' }}>Im Projektrechte-Verkauf enthalten:</p>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px 30px' }}>
      {items.map((item) => (
        <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: '#FFFFFF' }}>
          <CheckCircle2 size={12} color="#E07000" strokeWidth={2.7} />
          <span>{item}</span>
        </div>
      ))}
    </div>
  </div>
);

const Timeline = ({ milestones }) => (
  <div style={{ position: 'relative', padding: '8px 8px 0', marginBottom: '32px' }}>
    <div style={{ position: 'absolute', left: '54px', right: '54px', top: '27px', height: '2px', background: 'linear-gradient(to right, #22C55E 0%, #22C55E 56%, #D9E2EC 56%, #D9E2EC 100%)' }} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', position: 'relative', zIndex: 1 }}>
      {milestones.map(([title, date], index) => {
        const done = index < 4;
        const active = index === 4;
        return (
          <div key={title} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 0 }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: active ? '#E07000' : '#FFFFFF', border: `2px solid ${active ? '#E07000' : done ? '#22C55E' : '#C7D3E2'}`, display: 'grid', placeItems: 'center', color: active ? '#FFFFFF' : done ? '#22C55E' : '#C7D3E2', fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 900 }}>
              {active ? '›' : done ? '✓' : ''}
            </div>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.3px', fontWeight: 800, color: active ? '#E07000' : '#06265E', margin: '9px 0 2px', lineHeight: 1.15, whiteSpace: 'normal' }}>{title}</p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '6.8px', color: '#64748B', margin: 0 }}>{date}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const RiskList = ({ risks }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    {risks.map(([title, level, desc]) => (
      <div key={title} style={{ border: '1px solid #D8E1EC', background: '#F8FAFC', borderRadius: '8px', padding: '11px 15px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '5px' }}>
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.8px', fontWeight: 800, color: '#06265E', margin: 0 }}>{title}</p>
          <span style={{ background: '#ECFDF3', color: '#16A34A', borderRadius: '4px', padding: '3px 8px', fontFamily: 'Manrope, sans-serif', fontSize: '7px', fontWeight: 800 }}>{level}</span>
        </div>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.6px', lineHeight: 1.36, color: '#64748B', margin: 0 }}>{desc}</p>
      </div>
    ))}
  </div>
);

const RightsHighlights = ({ highlights }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
    {highlights.map(([Icon, title, desc]) => (
      <div key={title} style={{ minHeight: '58px', border: '1px solid #D8E1EC', borderRadius: '8px', background: '#F8FAFC', padding: '10px 13px', display: 'flex', alignItems: 'center', gap: '13px' }}>
        <div style={{ width: '30px', height: '30px', borderRadius: '7px', background: '#FFF7ED', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <Icon size={14} color="#E07000" strokeWidth={2.2} />
        </div>
        <div>
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.8px', fontWeight: 850, color: '#06265E', margin: '0 0 3px' }}>{title}</p>
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.6px', lineHeight: 1.35, color: '#64748B', margin: 0 }}>{desc}</p>
        </div>
      </div>
    ))}
  </div>
);

const ProjectRightsPage2Layout = ({ variantConfig, offerData, dragProps, inlineEditMode = false, onFieldChange }) => (
  <div className="a4-page" data-testid={`page-2-${variantConfig.prefix.toLowerCase()}`} style={projectRightsPageStyle}>
    <ProjectRightsHeader projectName={getProjectName(offerData, variantConfig)} variant={variantConfig} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
    <ProjectRightsContent top={30}>
      <DraggableElement id={`${variantConfig.prefix}-S2-Heading`} {...dragProps} initialStyle={{ width: '100%' }}>
        <RightsKpiStrip items={variantConfig.page2Kpis} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
      </DraggableElement>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '21px' }}>
        <DraggableElement id={`${variantConfig.prefix}-S2-Intro`} {...dragProps} initialStyle={{ width: '100%' }}>
          <RightsSectionHeading>{variantConfig.commercialTitle}</RightsSectionHeading>
          <RightsTable
            rows={variantConfig.commercialRows}
            data={offerData}
            valueAccent={(item) => item.label === 'Gesamtpreis' || item.label === 'Kaufpreis Turnkey' || item.label === 'Preis pro kWp'}
            inlineEditMode={inlineEditMode}
            onFieldChange={onFieldChange}
          />
        </DraggableElement>
        <DraggableElement id={`${variantConfig.prefix}-S2-Metrics`} {...dragProps} initialStyle={{ width: '100%' }}>
          <RightsSectionHeading>Vertragsdetails</RightsSectionHeading>
          <RightsTable rows={variantConfig.contractRows} data={offerData} valueAccent={(item) => item.label === 'Pachtvertrag'} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '21px', marginTop: '18px' }}>
        <DraggableElement id={`${variantConfig.prefix}-S3-Heading`} {...dragProps} initialStyle={{ width: '100%' }}>
          <RightsSectionHeading>{variantConfig.page2LeftTitle}</RightsSectionHeading>
          <RightsTable rows={variantConfig.page2LeftRows} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
        <DraggableElement id={`${variantConfig.prefix}-S3-Checklist`} {...dragProps} initialStyle={{ width: '100%' }}>
          <div style={{ marginTop: '34px' }}>
            <ContactCard data={offerData} large inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
          </div>
        </DraggableElement>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '21px', marginTop: '18px' }}>
        <DraggableElement id={`${variantConfig.prefix}-S3-Deal`} {...dragProps} initialStyle={{ width: '100%' }}>
          <RightsSectionHeading>{variantConfig.page2RightTitle}</RightsSectionHeading>
          <StatusCard rows={variantConfig.page2RightRows} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
        <DraggableElement id={`${variantConfig.prefix}-S4-Process`} {...dragProps} initialStyle={{ width: '100%' }}>
          <RightsSectionHeading>{variantConfig.networkTitle}</RightsSectionHeading>
          <RightsTable rows={variantConfig.networkRows} data={offerData} valueAccent={(item) => item.label === 'Netzanschlusszusage' || item.label === 'Anschlussleistung'} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
      </div>
    </ProjectRightsContent>
    <ProjectRightsFooter page={2} variant={variantConfig} />
  </div>
);

const ProjectRightsPage3Layout = ({ variantConfig, offerData, dragProps, inlineEditMode = false, onFieldChange }) => (
  <div className="a4-page" data-testid={`page-3-${variantConfig.prefix.toLowerCase()}`} style={projectRightsPageStyle}>
    <ProjectRightsHeader projectName={getProjectName(offerData, variantConfig)} variant={variantConfig} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
    <ProjectRightsContent top={35}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '21px' }}>
        <DraggableElement id={`${variantConfig.prefix}-S3-Heading`} {...dragProps} initialStyle={{ width: '100%' }}>
          <RightsSectionHeading>{variantConfig.page2LeftTitle}</RightsSectionHeading>
          <RightsTable rows={variantConfig.page2LeftRows} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
        <div>
          <DraggableElement id={`${variantConfig.prefix}-S3-Deal`} {...dragProps} initialStyle={{ width: '100%' }}>
            <RightsSectionHeading>{variantConfig.page2RightTitle}</RightsSectionHeading>
            <StatusCard rows={variantConfig.page2RightRows} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
          </DraggableElement>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '21px', marginTop: '20px' }}>
        <DraggableElement id={`${variantConfig.prefix}-S3-Commercial`} {...dragProps} initialStyle={{ width: '100%' }}>
          <RightsSectionHeading>{variantConfig.page3Title}</RightsSectionHeading>
          <RightsTable rows={variantConfig.page3Rows} data={offerData} valueAccent={(item) => item.label === 'Gesamtleistung' || item.label === 'Jahresertrag'} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
        <DraggableElement id={`${variantConfig.prefix}-S3-Checklist`} {...dragProps} initialStyle={{ width: '100%' }}>
          <div style={{ marginTop: '35px' }}>
            <ContactCard data={offerData} large inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
          </div>
        </DraggableElement>
      </div>
    </ProjectRightsContent>
    <ProjectRightsFooter page={3} variant={variantConfig} />
  </div>
);

const ProjectRightsPage4Layout = ({ variantConfig, offerData, dragProps, page = 4, inlineEditMode = false, onFieldChange }) => (
  <div className="a4-page" data-testid={`page-${page}-${variantConfig.prefix.toLowerCase()}`} style={projectRightsPageStyle}>
    <ProjectRightsHeader projectName={getProjectName(offerData, variantConfig)} variant={variantConfig} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
    {/* Oberer Abstand bewusst kleiner als auf den übrigen Seiten: Inhalt + Lageplan (16:9)
        überschreiten sonst die Fußzeilenlinie. Verschiebt den gesamten Block nach oben,
        die Abstände der Elemente zueinander bleiben unverändert. */}
    <ProjectRightsContent top={15}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '21px' }}>
        <div>
          <DraggableElement id={`${variantConfig.prefix}-S4-Heading`} {...dragProps} initialStyle={{ width: '100%' }}>
            <RightsSectionHeading>{variantConfig.pvsolTitle}</RightsSectionHeading>
            <RightsTable rows={variantConfig.pvsolRows} data={offerData} valueAccent={(item) => item.label === 'PV-Generatorleistung' || item.label === 'Spez. Jahresertrag' || item.label === 'Netzeinspeisung'} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
          </DraggableElement>
          <DraggableElement id={`${variantConfig.prefix}-S4-Final`} {...dragProps} initialStyle={{ width: '100%' }}>
            <div style={{ marginTop: '20px' }}>
              <RightsSectionHeading>{variantConfig.staticsTitle}</RightsSectionHeading>
              <RightsTable rows={variantConfig.staticsRows} data={offerData} valueAccent={(item) => item.label === 'Statik-Vorprüfung durch EN:POWER' || item.label === 'Statik-Gutachten' || item.label === 'Freigabe Statiker'} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
            </div>
          </DraggableElement>
        </div>
        <div>
          <DraggableElement id={`${variantConfig.prefix}-S4-Contact`} {...dragProps} initialStyle={{ width: '100%' }}>
            <div>
              <RightsSectionHeading>{variantConfig.landRegisterTitle}</RightsSectionHeading>
              <RightsTable rows={variantConfig.landRegisterRows} data={offerData} valueAccent={(item) => item.label === 'Rangrücktritt' || item.label === 'Grundbucheintragung'} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
            </div>
          </DraggableElement>
        </div>
      </div>
      <DraggableElement id={`${variantConfig.prefix}-S2-Benefits`} {...dragProps} initialStyle={{ width: '100%' }}>
        <div style={{ marginTop: '22px' }}>
          {!isFieldHidden(offerData, 'lageplanImage') ? (
            <>
              <RightsSectionHeading>Lageplan</RightsSectionHeading>
              <LageplanPlaceholder image={firstValue(offerData, ['lageplanImage', 'lageplan', 'lageplanBild'], '')} />
            </>
          ) : null}
        </div>
      </DraggableElement>
    </ProjectRightsContent>
    <ProjectRightsFooter page={page} variant={variantConfig} />
  </div>
);

const cashflowCellKeys = ['jahr', 'ertrag', 'erloes', 'opex', 'netto'];

const CashflowTable = ({ rows, data = {}, inlineEditMode = false, onFieldChange }) => {
  // Sobald das Projekt eigene Cashflow-Daten hat, nur Zeilen mit gesetztem Jahr
  // rendern und nicht mehr auf die statischen Beispielzellen zurückfallen.
  const hasProjectCashflow = rows.some((_, index) => String(data?.[`cashflow_${index + 1}_jahr`] ?? '').trim() !== '');
  const visibleRows = rows
    .map((cells, index) => ({ cells, index }))
    .filter(({ index }) => !hasProjectCashflow || String(data?.[`cashflow_${index + 1}_jahr`] ?? '').trim() !== '');
  return (
  <div style={rightsCardStyle}>
    <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(4, 1fr)', background: '#122A5C', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontSize: '7.6px', fontWeight: 850, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {['Jahr', 'Ertrag (kWh)', 'Erlös (€)', 'OPEX (€)', 'Netto (€)'].map((head) => (
        <div key={head} style={{ padding: '9px 12px' }}>{head}</div>
      ))}
    </div>
    {visibleRows.map(({ cells, index }, rowIndex) => (
      <div key={`${cells[0]}-${index}`} style={{ display: 'grid', gridTemplateColumns: '52px repeat(4, 1fr)', minHeight: '30px', background: rowIndex % 2 ? '#FFFFFF' : '#F8FAFC', borderTop: '1px solid #E6EDF5', fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#06265E', fontWeight: 750 }}>
        {cells.map((cell, cellIndex) => {
          const field = `cashflow_${index + 1}_${cashflowCellKeys[cellIndex]}`;
          if (isFieldHidden(data, field)) return <div key={`${field}-hidden`} />;
          return (
            <div key={`${field}-${cellIndex}`} style={{ padding: '8px 12px', textAlign: cellIndex === 0 ? 'left' : 'right' }}>
              <EditableValue
                field={field}
                value={firstValue(data, [field], hasProjectCashflow ? '' : cell)}
                inlineEditMode={inlineEditMode}
                onFieldChange={onFieldChange}
              />
            </div>
          );
        })}
      </div>
    ))}
  </div>
  );
};

const TurnkeySummaryCards = ({ items, data, inlineEditMode = false, onFieldChange }) => {
  const hideEmpty = useContext(HideEmptyFieldsContext);
  const base = visibleItems(items, data);
  const visible = hideEmpty ? base.filter((item) => {
    const resolved = firstValue(data, item.keys, item.fallback);
    return resolved !== '' && resolved != null;
  }) : base;
  if (!visible.length) return null;
  return (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(4, visible.length)}, 1fr)`, gap: '9px', marginBottom: '16px' }}>
    {visible.map((item, index) => (
      <div key={item.label} style={{ height: '74px', border: '1px solid #D8E1EC', borderRadius: '9px', background: index === 0 ? '#122A5C' : '#FFFFFF', padding: '13px 15px', boxSizing: 'border-box' }}>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.4px', fontWeight: 850, color: index === 0 ? '#FDBA74' : '#415675', textTransform: 'uppercase', letterSpacing: '0.055em', margin: '0 0 9px' }}>{summaryCardLabel(item, data)}</p>
        <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', lineHeight: 1, fontWeight: 800, color: index === 0 ? '#FFFFFF' : '#06265E', margin: 0 }}>
          <EditableValue
            field={rowFieldKey(item)}
            value={withSuffix(firstValue(data, item.keys, item.fallback), item.suffix)}
            inlineEditMode={inlineEditMode}
            onFieldChange={onFieldChange}
            fixedSuffix={item.suffix}
          />
        </p>
      </div>
    ))}
  </div>
  );
};

const TurnkeyFinancialPageLayout = ({ variantConfig, offerData, dragProps, inlineEditMode = false, onFieldChange }) => (
  <div className="a4-page" data-testid={`page-4-${variantConfig.prefix.toLowerCase()}`} style={projectRightsPageStyle}>
    <ProjectRightsHeader projectName={getProjectName(offerData, variantConfig)} variant={variantConfig} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
    <ProjectRightsContent top={32}>
      <DraggableElement id={`${variantConfig.prefix}-S5-Heading`} {...dragProps} initialStyle={{ width: '100%' }}>
        <RightsSectionHeading>Ertragsprognose & Cashflow</RightsSectionHeading>
        <TurnkeySummaryCards items={variantConfig.summaryCards} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        <CashflowTable rows={variantConfig.cashflow} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
      </DraggableElement>
      <div style={{ display: 'grid', gridTemplateColumns: '0.92fr 1.08fr', gap: '21px', marginTop: '22px' }}>
        <DraggableElement id={`${variantConfig.prefix}-S5-Process`} {...dragProps} initialStyle={{ width: '100%' }}>
          <RightsSectionHeading>OPEX-Aufschlüsselung</RightsSectionHeading>
          <RightsTable rows={variantConfig.opexRows} data={offerData} valueAccent={(item) => item.label === 'Gesamt OPEX p.a.'} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
        <DraggableElement id={`${variantConfig.prefix}-S5-Contact`} {...dragProps} initialStyle={{ width: '100%' }}>
          <RightsSectionHeading>{variantConfig.highlightsTitle}</RightsSectionHeading>
          <RightsHighlights highlights={resolveHighlights(variantConfig, offerData)} />
        </DraggableElement>
      </div>
    </ProjectRightsContent>
    <ProjectRightsFooter page={4} variant={variantConfig} />
  </div>
);

export const ProjectExposeCover = ({ data = {}, variant = 'expose-projektverkauf', editMode = false, showMarkers = true, selectedElements = [], elementPositions = {}, onSelectElement, onPositionChange, inlineEditMode = false, onFieldChange, hideEmptyFields = true }) => {
  const variantConfig = getVariant(variant);
  const offerData = mergeExposeData(data);
  const dragProps = dragPropsFor(editMode, showMarkers, selectedElements, elementPositions, onSelectElement, onPositionChange);
  const projectName = isFieldHidden(offerData, 'projektname') ? '' : getProjectName(offerData, variantConfig);

  return (
    <HideEmptyFieldsContext.Provider value={hideEmptyFields}>
    <div className="a4-page" data-testid={`cover-page-${variantConfig.prefix.toLowerCase()}`} style={{ background: '#0E1E3C', overflow: 'hidden' }}>
      <DraggableElement id={`${variantConfig.prefix}-S1-Deckblatt`} {...dragProps} initialStyle={{ width: '100%', height: '100%' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '52%', overflow: 'hidden' }}>
          {!isFieldHidden(offerData, 'projectImage') ? (
            <img src={getHeroImage(offerData)} alt="Projektbild aus Bitrix Deal" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center', display: 'block', filter: 'brightness(0.75) saturate(0.9)' }} />
          ) : null}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(14,30,60,0.22) 0%, transparent 25%, rgba(14,30,60,0.65) 72%, #0E1E3C 100%)' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '96px', background: 'linear-gradient(to bottom, #0E1E3C 0%, rgba(14,30,60,0.58) 64px, transparent 100%)' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: 'linear-gradient(to bottom, #E07000, rgba(224,112,0,0.2))' }} />
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '52%', background: 'linear-gradient(160deg, #122A5C 0%, #0E1E3C 100%)' }} />
        <div style={{ position: 'relative', zIndex: 10, height: '100%', display: 'flex', flexDirection: 'column', padding: '32px 48px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <EnPowerLogoText size="lg" variant="light" />
            <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', padding: '5px 14px', fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 800, color: 'rgba(255,255,255,0.84)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{variantConfig.badge}</div>
          </div>

          <div style={{ flex: '0 0 30%' }} />
          <div style={{ flexShrink: 0 }}>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#E07000', margin: '0 0 12px' }}>{variantConfig.eyebrow}</p>
            <h1 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: variantConfig.key === 'turnkey' ? '54px' : '50px', color: '#FFFFFF', letterSpacing: '0', lineHeight: 0.95, margin: '0 0 18px', textShadow: '0 2px 24px rgba(0,0,0,0.5)', whiteSpace: 'pre-line' }}>{variantConfig.headline}</h1>
            {projectName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '52px', height: '3px', background: '#E07000', borderRadius: '2px', flexShrink: 0 }} />
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '13.5px', fontWeight: 600, color: '#FFFFFF', margin: 0 }}>
                  <EditableValue
                    field={variantConfig.key === 'turnkey' ? 'turnkeyProjektname' : 'projektname'}
                    value={projectName}
                    inlineEditMode={inlineEditMode}
                    onFieldChange={onFieldChange}
                  />
                </p>
              </div>
            ) : null}
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: '36px' }}>
            <div style={{ marginBottom: '16px' }}>
              <KpiStrip items={variantConfig.coverKpis} data={offerData} columns={3} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
            </div>
            {!isFieldHidden(offerData, 'kundenadresse') ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', padding: '8px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '7px' }}>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 800, color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Projektstandort</span>
                <div style={{ width: '1px', height: '12px', background: 'rgba(255,255,255,0.15)' }} />
                <EditableValue
                  field="kundenadresse"
                  value={firstValue(offerData, ['kundenadresse', 'standort'], DEFAULT_DATA.kundenadresse)}
                  inlineEditMode={inlineEditMode}
                  onFieldChange={onFieldChange}
                  style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10.5px', fontWeight: 600, color: 'rgba(255,255,255,0.92)' }}
                />
              </div>
            ) : null}
            <CoverMeta variant={variantConfig} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.03em', margin: '14px 0 0' }}>
              Dieses Exposé ist vertraulich und wurde ausschließlich für den benannten Empfänger erstellt. · <EnPowerWordmarkInline enColor="rgba(255,255,255,0.28)" powerColor="#E07000" fontFamily="Manrope, sans-serif" fontSize="7px" fontWeight={800} />{!isFieldHidden(offerData, 'email') ? <> · <EditableValue field="email" value={firstValue(offerData, ['email'], DEFAULT_DATA.email)} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} /></> : null}
            </p>
          </div>
        </div>
      </DraggableElement>
    </div>
    </HideEmptyFieldsContext.Provider>
  );
};

export const ProjectExposePage2 = ({ data = {}, variant = 'expose-projektverkauf', editMode = false, showMarkers = true, selectedElements = [], elementPositions = {}, onSelectElement, onPositionChange, inlineEditMode = false, onFieldChange, hideEmptyFields = true }) => {
  const variantConfig = getVariant(variant);
  const offerData = mergeExposeData(data);
  const dragProps = dragPropsFor(editMode, showMarkers, selectedElements, elementPositions, onSelectElement, onPositionChange);
  const projectName = isFieldHidden(offerData, 'projektname') ? '' : getProjectName(offerData, variantConfig);

  if (variantConfig.key === 'projektrechte' || variantConfig.key === 'turnkey') {
    return <HideEmptyFieldsContext.Provider value={hideEmptyFields}><ProjectRightsPage2Layout variantConfig={variantConfig} offerData={offerData} dragProps={dragProps} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} /></HideEmptyFieldsContext.Provider>;
  }

  return (
    <HideEmptyFieldsContext.Provider value={hideEmptyFields}>
    <div className="a4-page" data-testid={`page-2-${variantConfig.prefix.toLowerCase()}`} style={pageStyle}>
      <PageHeader variant={variantConfig} title={projectName} />
      <DraggableElement id={`${variantConfig.prefix}-S2-Heading`} {...dragProps}>
        <div style={{ padding: '0 48px 18px' }}>
          <KpiStrip items={variantConfig.page2Kpis} data={offerData} columns={variantConfig.key === 'turnkey' ? 5 : 4} compact inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </div>
      </DraggableElement>
      <div style={{ padding: '0 48px', display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: '20px' }}>
        <DraggableElement id={`${variantConfig.prefix}-S2-Intro`} {...dragProps}>
          <SectionHeading>{variantConfig.page2LeftTitle}</SectionHeading>
          <DataTable rows={dataRows(variantConfig.page2LeftRows, offerData)} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
        <div>
          <DraggableElement id={`${variantConfig.prefix}-S2-Metrics`} {...dragProps}>
            <SectionHeading>{variantConfig.page2RightTitle}</SectionHeading>
            <DataTable rows={dataRows(variantConfig.page2RightRows, offerData)} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
          </DraggableElement>
          {variantConfig.key === 'turnkey' ? (
            <DraggableElement id={`${variantConfig.prefix}-S2-Benefits`} {...dragProps}>
              <div style={{ marginTop: '18px' }}>
                <SectionHeading>{variantConfig.performanceTitle}</SectionHeading>
                <DataTable rows={dataRows(variantConfig.performanceRows, offerData)} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
              </div>
            </DraggableElement>
          ) : !isFieldHidden(offerData, 'lageplanImage') ? (
            <DraggableElement id={`${variantConfig.prefix}-S2-Benefits`} {...dragProps}>
              <div style={{ marginTop: '18px' }}>
                <SectionHeading>Lageplan</SectionHeading>
                <LageplanPlaceholder image={firstValue(offerData, ['lageplanImage', 'lageplan', 'lageplanBild'], '')} />
              </div>
            </DraggableElement>
          ) : null}
        </div>
      </div>
      <Footer page={2} variant={variantConfig} />
    </div>
    </HideEmptyFieldsContext.Provider>
  );
};

export const ProjectExposePage3 = ({ data = {}, variant = 'expose-projektverkauf', editMode = false, showMarkers = true, selectedElements = [], elementPositions = {}, onSelectElement, onPositionChange, inlineEditMode = false, onFieldChange, hideEmptyFields = true }) => {
  const variantConfig = getVariant(variant);
  const offerData = mergeExposeData(data);
  const dragProps = dragPropsFor(editMode, showMarkers, selectedElements, elementPositions, onSelectElement, onPositionChange);
  const projectName = isFieldHidden(offerData, 'projektname') ? '' : getProjectName(offerData, variantConfig);

  if (variantConfig.key === 'projektrechte' || variantConfig.key === 'turnkey') {
    return <HideEmptyFieldsContext.Provider value={hideEmptyFields}><ProjectRightsPage4Layout variantConfig={variantConfig} offerData={offerData} dragProps={dragProps} page={3} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} /></HideEmptyFieldsContext.Provider>;
  }

  if (variantConfig.key === 'turnkey') {
    return (
      <HideEmptyFieldsContext.Provider value={hideEmptyFields}>
      <div className="a4-page" data-testid={`page-3-${variantConfig.prefix.toLowerCase()}`} style={pageStyle}>
        <PageHeader variant={variantConfig} title={projectName} />
        <DraggableElement id={`${variantConfig.prefix}-S3-Heading`} {...dragProps}>
          <div style={{ padding: '0 48px 12px' }}>
            <SectionHeading>Ertragsprognose & Cashflow</SectionHeading>
            <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(4, 1fr)', background: '#122A5C', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {['Jahr', 'Ertrag (kWh)', 'Erlös (€)', 'OPEX (€)', 'Netto (€)'].map((head) => <div key={head} style={{ padding: '9px 10px' }}>{head}</div>)}
              </div>
              {variantConfig.cashflow.map((cells, index) => (
                <div key={cells[0]} style={{ display: 'grid', gridTemplateColumns: '52px repeat(4, 1fr)', background: index % 2 ? '#FFFFFF' : '#F8FAFC', borderTop: '1px solid #E2E8F0', fontFamily: 'Manrope, sans-serif', fontSize: '9px', color: '#334155', fontWeight: 650 }}>
                  {cells.map((cell) => <div key={cell} style={{ padding: '8px 10px' }}>{cell}</div>)}
                </div>
              ))}
            </div>
          </div>
        </DraggableElement>
        <div style={{ padding: '0 48px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
          <DraggableElement id={`${variantConfig.prefix}-S3-Deal`} {...dragProps}>
            <SectionHeading>Erlösstruktur</SectionHeading>
            <DataTable rows={dataRows(variantConfig.revenueRows, offerData)} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
          </DraggableElement>
          <DraggableElement id={`${variantConfig.prefix}-S3-Commercial`} {...dragProps}>
            <SectionHeading>OPEX-Aufschlüsselung</SectionHeading>
            <DataTable rows={dataRows(variantConfig.opexRows, offerData)} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
          </DraggableElement>
        </div>
        <DraggableElement id={`${variantConfig.prefix}-S3-Checklist`} {...dragProps}>
          <div style={{ padding: '24px 48px 0', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            {variantConfig.summaryCards.map((item) => (
              <div key={item.label} style={{ background: '#122A5C', borderRadius: '10px', padding: '16px 14px', minHeight: '96px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.5px', fontWeight: 800, color: '#E07000', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>{summaryCardLabel(item, offerData)}</p>
                <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '20px', fontWeight: 800, color: '#FFFFFF', margin: 0 }}>
                  <EditableValue field={rowFieldKey(item)} value={withSuffix(firstValue(offerData, item.keys, item.fallback), item.suffix)} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} fixedSuffix={item.suffix} />
                </p>
              </div>
            ))}
          </div>
        </DraggableElement>
        <Footer page={3} variant={variantConfig} />
      </div>
      </HideEmptyFieldsContext.Provider>
    );
  }

  return (
    <HideEmptyFieldsContext.Provider value={hideEmptyFields}>
    <div className="a4-page" data-testid={`page-3-${variantConfig.prefix.toLowerCase()}`} style={pageStyle}>
      <PageHeader variant={variantConfig} title={projectName} />
      <div style={{ padding: '0 48px', display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: '20px' }}>
        <DraggableElement id={`${variantConfig.prefix}-S3-Heading`} {...dragProps}>
          <SectionHeading>{variantConfig.page3Title}</SectionHeading>
          <DataTable rows={dataRows(variantConfig.page3Rows, offerData)} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
        <div>
          <DraggableElement id={`${variantConfig.prefix}-S3-Deal`} {...dragProps}>
            <SectionHeading>{variantConfig.commercialTitle}</SectionHeading>
            <DataTable rows={dataRows(variantConfig.commercialRows, offerData)} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
          </DraggableElement>
          <DraggableElement id={`${variantConfig.prefix}-S3-Commercial`} {...dragProps}>
            <div style={{ marginTop: '18px' }}>
              <SectionHeading>Bestehende Verträge</SectionHeading>
              <DataTable rows={dataRows(variantConfig.contractRows, offerData)} data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
            </div>
          </DraggableElement>
        </div>
      </div>
      <DraggableElement id={`${variantConfig.prefix}-S3-Checklist`} {...dragProps}>
        <div style={{ margin: '24px 48px 0', borderRadius: '12px', background: '#122A5C', padding: '18px 22px' }}>
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '15px', fontWeight: 800, color: '#FFFFFF', margin: '0 0 12px' }}>Im Projektrechte-Verkauf enthalten:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 14px' }}>
            {variantConfig.included.map((item) => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '7px', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontSize: '8.8px', fontWeight: 650 }}>
                <CheckCircle2 size={12} color="#E07000" /> {item}
              </div>
            ))}
          </div>
        </div>
      </DraggableElement>
      <Footer page={3} variant={variantConfig} />
    </div>
    </HideEmptyFieldsContext.Provider>
  );
};

export const ProjectExposePage4 = ({ data = {}, variant = 'expose-projektverkauf', editMode = false, showMarkers = true, selectedElements = [], elementPositions = {}, onSelectElement, onPositionChange, inlineEditMode = false, onFieldChange, hideEmptyFields = true }) => {
  const variantConfig = getVariant(variant);
  const offerData = mergeExposeData(data);
  const dragProps = dragPropsFor(editMode, showMarkers, selectedElements, elementPositions, onSelectElement, onPositionChange);
  const projectName = isFieldHidden(offerData, 'projektname') ? '' : getProjectName(offerData, variantConfig);

  if (variantConfig.key === 'turnkey') {
    return <HideEmptyFieldsContext.Provider value={hideEmptyFields}><TurnkeyFinancialPageLayout variantConfig={variantConfig} offerData={offerData} dragProps={dragProps} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} /></HideEmptyFieldsContext.Provider>;
  }

  if (variantConfig.key === 'projektrechte') {
    return <HideEmptyFieldsContext.Provider value={hideEmptyFields}><ProjectRightsPage4Layout variantConfig={variantConfig} offerData={offerData} dragProps={dragProps} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} /></HideEmptyFieldsContext.Provider>;
  }

  if (variantConfig.key === 'turnkey') {
    return (
      <HideEmptyFieldsContext.Provider value={hideEmptyFields}>
      <div className="a4-page" data-testid={`page-4-${variantConfig.prefix.toLowerCase()}`} style={pageStyle}>
        <PageHeader variant={variantConfig} title={projectName} />
        <div style={{ padding: '0 48px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <DraggableElement id={`${variantConfig.prefix}-S4-Heading`} {...dragProps}>
            <SectionHeading>Standort & Lageplan</SectionHeading>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontFamily: 'Manrope, sans-serif', fontSize: '9px', fontWeight: 650, marginBottom: '10px' }}>
              <MapPin size={12} color="#E07000" /> {firstValue(offerData, ['kundenadresse', 'standort'], DEFAULT_DATA.kundenadresse)}
            </div>
            <div style={{ height: '188px', border: '1px dashed #CBD5E1', borderRadius: '10px', background: '#F8FAFC', display: 'grid', placeItems: 'center', textAlign: 'center', color: '#94A3B8', fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 800 }}>
              Lageplan / Luftbild wird hier eingefügt
            </div>
          </DraggableElement>
          <DraggableElement id={`${variantConfig.prefix}-S4-Process`} {...dragProps}>
            <SectionHeading>Übergabe & Dokumentation</SectionHeading>
            <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', background: '#FFFFFF' }}>
              {variantConfig.documentation.map(({ icon: Icon, text }, index) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderBottom: index < variantConfig.documentation.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                  <Icon size={12} color="#E07000" />
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', color: '#334155' }}>{text}</span>
                </div>
              ))}
            </div>
          </DraggableElement>
        </div>
        <DraggableElement id={`${variantConfig.prefix}-S4-Contact`} {...dragProps}>
          <div style={{ padding: '22px 48px 0' }}>
            <SectionHeading>{variantConfig.highlightsTitle}</SectionHeading>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
              {resolveHighlights(variantConfig, offerData).map(([Icon, title, desc]) => (
                <div key={title} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px 14px', minHeight: '82px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}><Icon size={13} color="#E07000" /><p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', fontWeight: 800, color: '#122A5C', margin: 0 }}>{title}</p></div>
                  <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.2px', lineHeight: 1.45, color: '#64748B', margin: 0 }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </DraggableElement>
        <DraggableElement id={`${variantConfig.prefix}-S4-Final`} {...dragProps}>
          <div style={{ margin: '22px 48px 0', background: 'linear-gradient(135deg, #0E1E3C 0%, #122A5C 100%)', borderRadius: '12px', padding: '18px 24px', display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 800, color: '#FFFFFF', margin: '0 0 4px' }}>Interesse? Sprechen Sie uns an.</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', color: 'rgba(255,255,255,0.64)', margin: 0 }}>Wir freuen uns auf Ihre Kontaktaufnahme für weitere Details oder eine Besichtigung.</p>
            </div>
            <ContactInline data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
          </div>
        </DraggableElement>
        <Footer page={4} variant={variantConfig} />
      </div>
      </HideEmptyFieldsContext.Provider>
    );
  }

  return (
    <HideEmptyFieldsContext.Provider value={hideEmptyFields}>
    <div className="a4-page" data-testid={`page-4-${variantConfig.prefix.toLowerCase()}`} style={pageStyle}>
      <PageHeader variant={variantConfig} title={projectName} />
      <DraggableElement id={`${variantConfig.prefix}-S4-Heading`} {...dragProps}>
        <div style={{ padding: '0 48px 18px' }}>
          <SectionHeading>Projekt-Meilensteine</SectionHeading>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
            {variantConfig.milestones.map(([title, date], index) => (
              <div key={title} style={{ background: index === 4 ? '#FFF7ED' : '#F8FAFC', border: `1px solid ${index === 4 ? '#FDBA74' : '#E2E8F0'}`, borderRadius: '8px', padding: '10px 8px', minHeight: '76px' }}>
                <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '15px', fontWeight: 800, color: index === 4 ? '#E07000' : '#122A5C', margin: '0 0 6px' }}>{String(index + 1).padStart(2, '0')}</p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.6px', fontWeight: 800, color: '#122A5C', margin: '0 0 5px', lineHeight: 1.25 }}>{title}</p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.2px', color: '#64748B', margin: 0 }}>{date}</p>
              </div>
            ))}
          </div>
        </div>
      </DraggableElement>
      <DraggableElement id={`${variantConfig.prefix}-S4-Process`} {...dragProps}>
        <div style={{ padding: '0 48px 18px' }}>
          <SectionHeading>Risikobewertung & Mitigierung</SectionHeading>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            {variantConfig.risks.map(([title, level, desc]) => (
              <div key={title} style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '11px 14px', background: '#FFFFFF' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', fontWeight: 800, color: '#122A5C', margin: 0 }}>{title}</p>
                  <span style={{ background: '#DCFCE7', color: '#047857', borderRadius: '999px', padding: '2px 7px', fontSize: '7px', fontWeight: 800 }}>{level}</span>
                </div>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.9px', lineHeight: 1.38, color: '#64748B', margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </DraggableElement>
      <div style={{ padding: '0 48px', display: 'grid', gridTemplateColumns: '1fr 260px', gap: '20px' }}>
        <DraggableElement id={`${variantConfig.prefix}-S4-Contact`} {...dragProps}>
          <SectionHeading>{variantConfig.highlightsTitle}</SectionHeading>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {resolveHighlights(variantConfig, offerData).map(([Icon, title, desc]) => (
              <div key={title} style={{ background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0', padding: '10px 14px', display: 'flex', gap: '10px' }}>
                <div style={{ width: '26px', height: '26px', borderRadius: '6px', background: 'rgba(224,112,0,0.08)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon size={13} color="#E07000" /></div>
                <div>
                  <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', fontWeight: 800, color: '#122A5C', margin: '0 0 2px' }}>{title}</p>
                  <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#64748B', margin: 0, lineHeight: 1.4 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </DraggableElement>
        <DraggableElement id={`${variantConfig.prefix}-S4-Final`} {...dragProps}>
          <ContactCard data={offerData} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </DraggableElement>
      </div>
      <Footer page={4} variant={variantConfig} />
    </div>
    </HideEmptyFieldsContext.Provider>
  );
};

const ContactCard = ({ data, large = false, inlineEditMode = false, onFieldChange }) => {
  const hideEmpty = useContext(HideEmptyFieldsContext);
  const showAnsprech = !isFieldHidden(data, 'ansprechpartner') && (!hideEmpty || firstValue(data, ['ansprechpartner'], DEFAULT_DATA.ansprechpartner));
  const showPosition = !isFieldHidden(data, 'position') && (!hideEmpty || firstValue(data, ['position'], DEFAULT_DATA.position));
  return (
    <div style={{ background: 'linear-gradient(135deg, #0E1E3C 0%, #122A5C 100%)', borderRadius: large ? '10px' : '12px', padding: large ? '19px 21px' : '16px 20px' }}>
      <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: large ? '13px' : '12px', fontWeight: 800, color: '#FFFFFF', margin: '0 0 10px' }}>Ihr Ansprechpartner</p>
      {showAnsprech ? (
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: large ? '9px' : '9.5px', fontWeight: 850, color: '#E07000', margin: '0 0 3px' }}>
          <EditableValue field="ansprechpartner" value={firstValue(data, ['ansprechpartner'], DEFAULT_DATA.ansprechpartner)} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </p>
      ) : null}
      {showPosition ? (
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: large ? '8px' : '8.5px', color: 'rgba(255,255,255,0.68)', margin: '0 0 11px' }}>
          <EditableValue field="position" value={firstValue(data, ['position'], DEFAULT_DATA.position)} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
        </p>
      ) : null}
      <ContactInline data={data} stacked inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} />
    </div>
  );
};

const ContactInline = ({ data, stacked = false, inlineEditMode = false, onFieldChange }) => {
  const hideEmpty = useContext(HideEmptyFieldsContext);
  const base = [
    [Phone, 'telefonnummer', firstValue(data, ['telefonnummer', 'telefon'], DEFAULT_DATA.telefonnummer)],
    [Mail, 'email', firstValue(data, ['email'], DEFAULT_DATA.email)],
    [Globe, 'website', firstValue(data, ['website'], DEFAULT_DATA.website)],
  ].filter(([, key]) => !isFieldHidden(data, key));
  const items = hideEmpty ? base.filter(([,, text]) => text !== '' && text != null) : base;
  return (
    <div style={{ display: 'flex', flexDirection: stacked ? 'column' : 'row', gap: stacked ? '4px' : '20px', flexWrap: 'wrap' }}>
      {items.map(([Icon, key, text]) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Icon size={stacked ? 10 : 11} color="#E07000" />
          <EditableValue
            field={key}
            value={text}
            inlineEditMode={inlineEditMode}
            onFieldChange={onFieldChange}
            style={{ fontFamily: 'Manrope, sans-serif', fontSize: stacked ? '8.5px' : '9px', fontWeight: 650, color: '#FFFFFF' }}
          />
        </div>
      ))}
    </div>
  );
};

const WirtKpiRow = ({ label, value: val, unit, accent }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 14px', borderBottom: '1px solid #E6EDF5', fontFamily: 'Manrope, sans-serif', fontSize: '9px' }}>
    <span style={{ color: '#415675', fontWeight: 600 }}>{label}</span>
    <span style={{ color: accent ? '#E07000' : '#06265E', fontWeight: 750 }}>{val}{unit ? ` ${unit}` : ''}</span>
  </div>
);

const WirtKpiCard = ({ label, val, unit, highlight }) => (
  <div style={{ border: '1px solid #D8E1EC', borderRadius: '9px', background: highlight ? '#122A5C' : '#FFFFFF', padding: '12px 15px', boxSizing: 'border-box' }}>
    <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7px', fontWeight: 850, color: highlight ? '#FDBA74' : '#415675', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 7px' }}>{label}</p>
    <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '17px', lineHeight: 1, fontWeight: 800, color: highlight ? '#FFFFFF' : '#06265E', margin: 0 }}>{val}{unit ? <span style={{ fontSize: '10px', fontWeight: 600, marginLeft: '3px' }}>{unit}</span> : null}</p>
  </div>
);

const WirtCashflowTablePage = ({ rows, pageNum, totalPages, variantConfig, offerData }) => {
  const headerCells = ['Jahr', 'Investition (€)', 'Betriebsk. (€)', 'Vergütung (€)', 'Cashflow (€)', 'Kumuliert (€)'];
  return (
    <div className="a4-page" data-testid={`page-wirt-cf-${pageNum}`} style={projectRightsPageStyle}>
      <ProjectRightsHeader projectName={getProjectName(offerData, variantConfig)} variant={variantConfig} data={offerData} />
      <ProjectRightsContent top={28}>
        <RightsSectionHeading>Cashflow — Jahr {rows[0]?.jahr} bis {rows[rows.length - 1]?.jahr}</RightsSectionHeading>
        <div style={{ ...rightsCardStyle, marginBottom: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '42px repeat(5, 1fr)', background: '#122A5C', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontSize: '7px', fontWeight: 850, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {headerCells.map((h) => <div key={h} style={{ padding: '7px 8px' }}>{h}</div>)}
          </div>
          {rows.map((row, i) => (
            <div key={row.jahr} style={{ display: 'grid', gridTemplateColumns: '42px repeat(5, 1fr)', background: i % 2 ? '#FFFFFF' : '#F8FAFC', borderTop: '1px solid #E6EDF5', fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#06265E', fontWeight: 700 }}>
              <div style={{ padding: '5px 8px' }}>{row.jahr}</div>
              <div style={{ padding: '5px 8px', textAlign: 'right', color: row.investition > 0 ? '#DC2626' : '#64748B' }}>{row.investition > 0 ? `-${fmtNum(row.investition)}` : '—'}</div>
              <div style={{ padding: '5px 8px', textAlign: 'right', color: '#64748B' }}>{row.betriebskosten > 0 ? fmtNum(row.betriebskosten) : '—'}</div>
              <div style={{ padding: '5px 8px', textAlign: 'right', color: '#059669' }}>{fmtNum(row.erloes)}</div>
              <div style={{ padding: '5px 8px', textAlign: 'right', color: row.jaehrlich >= 0 ? '#059669' : '#DC2626', fontWeight: 800 }}>{fmtNum(row.jaehrlich)}</div>
              <div style={{ padding: '5px 8px', textAlign: 'right', color: row.kumuliert >= 0 ? '#059669' : '#DC2626', fontWeight: 800 }}>{fmtNum(row.kumuliert)}</div>
            </div>
          ))}
        </div>
      </ProjectRightsContent>
      <ProjectRightsFooter page={pageNum} variant={variantConfig} />
    </div>
  );
};

function fmtNum(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtDec(n, digits = 2) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export const WirtschaftlichkeitsAnalyse = ({ data = {}, inlineEditMode = false, onFieldChange, hideEmptyFields = true, showLabels = false, labelClass = '' }) => {
  const variantConfig = VARIANTS.turnkey;
  const offerData = mergeExposeData(data);

  let cashflowData = [];
  try {
    const raw = offerData.wirtschaftlichkeit_cashflow_json;
    if (raw) cashflowData = JSON.parse(raw);
  } catch (_e) { /* ignore */ }

  let eegStufen = [];
  try {
    const raw = offerData.eeg_verguetungsstufen_json;
    if (raw) eegStufen = JSON.parse(raw);
  } catch (_e) { /* ignore */ }

  const kwp = parseFloat(firstValue(offerData, ['pvsol_generatorleistung', 'kwp_leistung'], '0')) || 0;
  const jahresertrag = parseFloat(firstValue(offerData, ['jahresertrag', 'pvsol_jahresertrag'], '0').replace(/\./g, '').replace(',', '.')) || 0;
  const netzeinspeisung = parseFloat(firstValue(offerData, ['netzeinspeisung', 'pvsol_netzeinspeisung'], '0').replace(/\./g, '').replace(',', '.')) || 0;
  const investition = parseFloat(firstValue(offerData, ['turnkey_kaufpreis'], '0').replace(/\./g, '').replace(',', '.')) || 0;
  const irr = firstValue(offerData, ['irr'], '—');
  const rendite = firstValue(offerData, ['rendite'], '');
  const eigenkapitalrendite = firstValue(offerData, ['eigenkapitalrendite'], '');
  const payback = firstValue(offerData, ['payback'], '—');
  const stromgestehungskosten = firstValue(offerData, ['stromgestehungskosten'], '');
  const netto25 = firstValue(offerData, ['netto_erloes_25j'], '—');
  const eegVerguetung = firstValue(offerData, ['eeg_verguetung'], '—');
  // EEG-Vergütung immer als deutsche Zahl mit Einheit ct/kWh ausgeben (Feldwert kann
  // je nach Quelle "10.55", "10,55" oder "10,55 ct/kWh" enthalten).
  const eegVergNum = parseGermanNumber(String(eegVerguetung));
  const eegVergDisplay = Number.isFinite(eegVergNum) ? `${fmtDec(eegVergNum, 2)} ct/kWh` : '—';
  const jahreserloes = firstValue(offerData, ['jahreserloes'], '—');
  const opex = firstValue(offerData, ['opex'], '—');
  const betrachtungszeitraum = firstValue(offerData, ['betrachtungszeitraum'], '20');
  const investProKwp = firstValue(offerData, ['turnkey_preis_kwp'], '—');
  const verguetungszeitraum = firstValue(offerData, ['verguetungszeitraum'], '');
  const inbetriebnahme = firstValue(offerData, ['inbetriebnahme_datum'], '') || autoInbetriebnahme();
  const eegTarifName = firstValue(offerData, ['eeg_tarif_name'], '');
  const gesamtverguetung = firstValue(offerData, ['gesamtverguetung'], '');
  const kapitalwert = firstValue(offerData, ['kapitalwert'], '');

  const rowsPerPage = 20;
  const cashflowPages = [];
  for (let i = 0; i < cashflowData.length; i += rowsPerPage) {
    cashflowPages.push(cashflowData.slice(i, i + rowsPerPage));
  }

  const wirtFooter = 'Wirtschaftlichkeitsanalyse • Vertraulich';
  const wirtBadge = 'Wirtschaftlichkeitsanalyse';

  const pageLabel = (number, title) => showLabels ? (
    <div className={labelClass} style={{ margin: '18px 0 8px', color: '#64748B', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      Seite {number} · {title}
    </div>
  ) : null;

  return (
    <HideEmptyFieldsContext.Provider value={hideEmptyFields}>
      {/* Page 1: Overview */}
      {pageLabel(1, 'Wirtschaftliche Kenngrößen')}
      <div className="a4-page" data-testid="page-wirt-overview" style={projectRightsPageStyle}>
        <ProjectRightsHeader projectName={getProjectName(offerData, variantConfig)} variant={{ ...variantConfig, badge: wirtBadge }} data={offerData} />
        <ProjectRightsContent top={28}>
          <RightsSectionHeading>Wirtschaftliche Kenngrößen</RightsSectionHeading>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '9px', marginBottom: '18px' }}>
            <WirtKpiCard label="Rendite p.a." val={rendite || irr} unit="%" highlight />
            <WirtKpiCard label="Amortisation" val={payback} unit="Jahre" />
            <WirtKpiCard label={`Netto-Erlös (${horizontJahre(offerData)} J.)`} val={fmtNum(parseGermanNumber(netto25) || 0)} unit="€" />
            <WirtKpiCard label="Stromgestehungsk." val={stromgestehungskosten ? fmtDec(parseGermanNumber(stromgestehungskosten) / 100, 4) : '—'} unit="€/kWh" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <RightsSectionHeading>Anlagendaten</RightsSectionHeading>
              <div style={rightsCardStyle}>
                <WirtKpiRow label="Netzeinspeisung (Jahr 1)" value={netzeinspeisung > 0 ? `${fmtNum(netzeinspeisung)} kWh/Jahr` : '—'} />
                <WirtKpiRow label="PV-Generatorleistung" value={`${fmtDec(kwp, 0)} kWp`} />
                <WirtKpiRow label="Betrachtungszeitraum" value={`${String(betrachtungszeitraum).replace(/\s*Jahre\s*/gi, '')} Jahre`} />
              </div>
            </div>
            <div>
              <RightsSectionHeading>Vergütungszeitraum</RightsSectionHeading>
              <div style={rightsCardStyle}>
                {inbetriebnahme ? <WirtKpiRow label="Inbetriebnahme" value={inbetriebnahme} /> : null}
                {verguetungszeitraum ? <WirtKpiRow label="Vergütungszeitraum" value={`${verguetungszeitraum} Jahre`} /> : null}
                {eegTarifName ? <WirtKpiRow label="EEG-Tarif" value={eegTarifName} /> : null}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <RightsSectionHeading>Zahlungsübersicht</RightsSectionHeading>
              <div style={rightsCardStyle}>
                <WirtKpiRow label="Investitionskosten" value={`${fmtNum(investition)} €`} accent />
                {investProKwp !== '—' ? <WirtKpiRow label="Spez. Investitionskosten" value={`${fmtDec(parseFloat(investProKwp), 2)} €/kWp`} /> : null}
                <WirtKpiRow label="Jährliche Kosten" value={opex !== '—' ? `${fmtDec(parseFloat(String(opex).replace(/\./g, '').replace(',', '.')), 2)} €/Jahr` : '—'} />
              </div>
            </div>
            <div>
              <RightsSectionHeading>Vergütung und Ersparnisse</RightsSectionHeading>
              <div style={rightsCardStyle}>
                <WirtKpiRow label="Jahresvergütung (Jahr 1)" value={`${fmtDec(parseFloat(String(jahreserloes).replace(/\./g, '').replace(',', '.')), 2)} €/Jahr`} />
                <WirtKpiRow label="EEG-Vergütung" value={eegVergDisplay} />
                {gesamtverguetung ? <WirtKpiRow label="Gesamtvergütung" value={`${fmtNum(parseFloat(gesamtverguetung))} €`} /> : null}
                {eigenkapitalrendite ? <WirtKpiRow label="Eigenkapitalrendite" value={`${eigenkapitalrendite} %`} /> : null}
                {kapitalwert ? <WirtKpiRow label="Kapitalwert (NPV)" value={`${fmtDec(parseGermanNumber(kapitalwert), 2)} €`} /> : null}
              </div>
            </div>
          </div>

          {eegStufen.length > 0 ? (
            <>
              <RightsSectionHeading>EEG-Vergütungsstufen</RightsSectionHeading>
              <div style={rightsCardStyle}>
                {eegStufen.filter(s => s.ct_kwh > 0).map((stufe, i) => (
                  <WirtKpiRow key={i} label={`ab ${stufe.ab_kwp} kWp`} value={`${fmtDec(stufe.ct_kwh, 2)} ct/kWh`} />
                ))}
              </div>
            </>
          ) : null}

        </ProjectRightsContent>
        <ProjectRightsFooter page={1} variant={{ ...variantConfig, footer: wirtFooter }} />
      </div>

      {/* Cashflow table pages */}
      {cashflowPages.map((rows, pageIdx) => (
        <React.Fragment key={pageIdx}>
          {pageLabel(pageIdx + 2, `Cashflow — Jahr ${rows[0]?.jahr} bis ${rows[rows.length - 1]?.jahr}`)}
          <WirtCashflowTablePage
            rows={rows}
            pageNum={pageIdx + 2}
            totalPages={cashflowPages.length + 1}
            variantConfig={{ ...variantConfig, badge: wirtBadge, footer: wirtFooter }}
            offerData={offerData}
          />
        </React.Fragment>
      ))}
    </HideEmptyFieldsContext.Provider>
  );
};

